import type {
  AccountingJournalLineResponse,
  ExtensionImportResponse,
  ExtensionInstallationSummary,
  ExtensionInvocationLog,
  ExtensionInvocationLogPage,
  ExtensionSourceDocumentCurrentResponse,
  ExtensionSourceDocumentMetadata,
  ExtensionSourceDocumentUploadMetadata,
  ExtensionSourceDocumentUploadResponse,
  FinanceBankRuleResponse,
  FinanceConversionBalanceResponse,
  FinanceConversionBalanceSaveRequest,
  FinanceDirectoryContactResponse,
  FinanceConversionLineRequest,
  FinanceBankRuleUpdateResponse,
  FinanceBankTransactionMatch,
  FinanceBankTransactionMatchResponse,
  FinanceBankTransactionResponse,
  FinanceExchangeRateListResponse,
  FinanceInvoicePostRequest,
  FinanceInvoicePostResponse,
  FinanceJournalEntryPostRequest,
  FinanceExchangeRateRecordRequest,
  FinanceExchangeRateResponse,
  FinanceExchangeRateResolveResponse,
  FinanceLowBalanceAlert,
  FinanceLowBalanceAlertListResponse,
  FinancePayrollJournalPostResponse,
  FinancePayrollJournalReviewEntry,
  FinancePayrollJournalReviewResponse,
  FinancePayrollJournalTaxLine,
  FinanceStatementReconciliationFinishResponse,
  FinanceStatementReconciliationReviewResponse,
  FinanceTaxEstimateQuoteRequest,
  FinanceTaxEstimateQuoteResponse,
  FinanceTaxEstimateReserveBooksRequest,
  FinanceTaxEstimateReserveManualRequest,
  FinanceTaxEstimateReserveResponse,
  FinanceTaxEstimateRule,
  FinancePaymentTermRequest,
  FinancePaymentTermResponse,
  FinanceStatementResponse,
  OrganizationResponse,
  LicenseSummary,
} from "@api-client";
import { API_BASE_PATH } from "@api-client";
import { nextIsoDay } from "@/lib/conversion-balances";
import { isUiFixtureMode, UI_FIXTURE_SCOPE } from "@/lib/dev-mode";


// Some write types remain locally modeled against the Rust handlers, but
// payment-term contracts reuse their generated TypeSpec request/response
// types from `@api-client`.

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? "";

// --- Session auth (cookie + CSRF, same pattern as apps/sylviq-www) -----
// `/*` requires either a browser session cookie + CSRF token, or a
// `Bearer` API token — there is no anonymous path
// (`backend/service/src/controllers.rs`, `authenticate()`).
// Sign-in belongs to the Sylviq platform (see lib/platform-signin.ts); this
// app only relies on the session cookie (HttpOnly, JS can't read it by design) with
// `credentials: "include"` on every request. The server hands back a
// separate CSRF token from `POST /auth/csrf` (cookie session required, no
// CSRF header needed; each call rotates the stored hash, so an older token
// held by another tab stops working). Every mutating request must echo it
// back as `x-csrf-token` (see `authenticate()`'s `mutation` check). It is
// cached in localStorage to survive a reload.

const CSRF_STORAGE_KEY = "niche.csrf_token";

export function getStoredCsrfToken(): string | null {
  try {
    return localStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeCsrfToken(token: string): void {
  try {
    localStorage.setItem(CSRF_STORAGE_KEY, token);
  } catch {
    // Storage blocked: the token still lives for the in-flight retry below.
  }
}

let csrfRefresh: Promise<string> | null = null;

/** Fetches (and stores) a fresh CSRF token for the current cookie session.
 * Concurrent callers share one call, since each call rotates the token. */
export function refreshCsrfToken(): Promise<string> {
  csrfRefresh ??= (async () => {
    try {
      const { csrf_token } = await request<{ csrf_token: string }>(
        "POST",
        "/auth/csrf",
        { skipCsrfRetry: true },
      );
      storeCsrfToken(csrf_token);
      return csrf_token;
    } finally {
      csrfRefresh = null;
    }
  })();
  return csrfRefresh;
}

export function clearStoredCsrfToken(): void {
  try {
    localStorage.removeItem(CSRF_STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage was never writable.
  }
}

// --- Dev fallback ----------------------------------------------------------
// `.env.local` (gitignored — never commit a real token) may still seed a
// Bearer token and a fixed organization scope for local development against a
// backend without going through the login UI:
//
// ```
// VITE_API_ORIGIN=http://localhost:PORT
// VITE_DEV_API_TOKEN=<API_TOKEN>
// VITE_DEV_ORGANIZATION_ID=<ORG_ID>
// VITE_DEV_LEGAL_ENTITY_ID=<LEGAL_ENTITY_ID>
// ```
//
// The committed login flow is primary: the Bearer header is only attached
// when `VITE_DEV_API_TOKEN` is set, and the env scope only seeds the stored
// organization scope until the onboarding wizard (or the legal-entity
// switcher) overwrites it. Production builds set none of these — see
// vite-env.d.ts.

function devBearerToken(): string | null {
  return import.meta.env.VITE_DEV_API_TOKEN ?? null;
}

// --- Organization scope -----------------------------------------------------
// Every finance call is scoped to (organization, legal entity) — the tenancy
// unit from docs/engineering/TENANCY_MODEL.md. Sylviq Console owns the
// organization choice and supplies it in the launch URL. Accounting resolves
// the organization's primary legal entity after validating the handoff. The
// header can switch to another entity in that same organization. A stale
// browser selection must never override Console's active organization.

let activeScope: OrganizationScope | null = null;

export interface OrganizationScope {
  // Index signature so a scope can be spread straight into a query map
  // (the old DevScope shape this replaced carried the same one).
  [key: string]: string;
  organization_id: string;
  legal_entity_id: string;
}

export type ExtensionInstallation = ExtensionInstallationSummary;

export function getStoredOrganizationScope(): Partial<OrganizationScope> {
  const query = new URLSearchParams(window.location.search);
  if (isUiFixtureMode) {
    const legal_entity_id = query.get("legal_entity_id")?.trim() ?? "";
    return {
      ...UI_FIXTURE_SCOPE,
      legal_entity_id: legal_entity_id || UI_FIXTURE_SCOPE.legal_entity_id,
    };
  }
  const organization_id = query.get("organization_id")?.trim() ?? "";
  const legal_entity_id = query.get("legal_entity_id")?.trim() ?? "";
  // An incomplete handoff is invalid. Do not combine a new organization with
  // a prior organization's identifiers or fall back to a browser-persisted
  // scope.
  if (!organization_id) {
    return { organization_id: "", legal_entity_id: "" };
  }
  return {
    organization_id,
    legal_entity_id,
  };
}

export function setStoredOrganizationScope(scope: OrganizationScope): void {
  activeScope = scope;
}

export function clearStoredOrganizationScope(): void {
  activeScope = null;
}

class ApiConfigError extends Error {}

// In-memory read-cache generation. Any successful mutation bumps it so
// pages holding cached reads (Overview today) can discard them without
// persisting balances, names, or any other finance data in browser storage.
let readCacheGeneration = 0;

export function getReadCacheGeneration(): number {
  return readCacheGeneration;
}

/** Drop every in-memory read cache after sign-out; cached finance rows are
 * never returned to a different session even within the same tab. */
export function invalidateReadCache(): void {
  readCacheGeneration += 1;
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    message: string,
  ) {
    super(message);
  }
}

/** Active (organization, legal entity) scope. Throws `ApiConfigError` when
 * the visitor hasn't picked an organization yet — callers surface that as
 * "finish onboarding first", and the `RequireOrganization` route guard keeps
 * it from happening in normal navigation. */
function currentOrganizationScope(): OrganizationScope {
  if (!activeScope) {
    throw new ApiConfigError(
      "No Console organization context is active — open Accounting from SYLVIQ Console",
    );
  }
  return activeScope;
}

interface RequestOptions {
  query?: Record<string, string | string[] | undefined>;
  body?: unknown;
  /** Extra request headers, merged after the CSRF/Content-Type defaults. */
  headers?: Record<string, string>;
  /** Internal: never refresh-and-retry on a CSRF 401 (no loops). */
  skipCsrfRetry?: boolean;
}

function buildUrl(
  path: string,
  query: Record<string, string | string[] | undefined> = {},
): URL {
  const url = new URL(
    `${API_ORIGIN}${API_BASE_PATH}${path}`,
    window.location.origin,
  );
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) continue;
    // Arrays append the key once per value so repeatable params
    // (`?compare_date=a&compare_date=b`) survive the trip.
    for (const item of Array.isArray(value) ? value : [value]) {
      url.searchParams.append(key, item);
    }
  }
  return url;
}

async function request<TResponse>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const url = buildUrl(path, options.query);

  if (isUiFixtureMode) {
    const { requestUiFixture } = await import("@/lib/ui-fixtures");
    const fixture = await requestUiFixture(method, url, options.body);
    if (fixture.status >= 400) {
      throw new ApiRequestError(
        fixture.status,
        path,
        `${method} ${path} failed: ${fixture.status}`,
      );
    }
    return fixture.body as TResponse;
  }

  const headers: Record<string, string> = {};
  if (options.body) headers["Content-Type"] = "application/json";
  // Cookie sessions require a CSRF token on mutations; Bearer-token auth
  // (api_tokens table) skips that check
  // (backend/service/src/controllers.rs `authenticate()`).
  if (method !== "GET") {
    const csrf = getStoredCsrfToken();
    if (csrf) headers["x-csrf-token"] = csrf;
  }
  // Dev fallback only — see the header comment. No VITE_DEV_API_TOKEN in
  // the build means cookie-session auth, full stop.
  const fallback = devBearerToken();
  if (fallback) headers.Authorization = `Bearer ${fallback}`;
  for (const [name, value] of Object.entries(options.headers ?? {})) {
    headers[name] = value;
  }

  const response = await fetch(url, {
    method,
    // Sends the HttpOnly session cookie on same-origin and on cross-origin
    // (VITE_API_ORIGIN) requests alike.
    credentials: "include",
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    if (
      response.status === 401 &&
      method !== "GET" &&
      !options.skipCsrfRetry &&
      !devBearerToken() &&
      /csrf/i.test(detail)
    ) {
      // Stale/rotated CSRF token: fetch a new one and retry exactly once.
      await refreshCsrfToken();
      return request<TResponse>(method, path, {
        ...options,
        skipCsrfRetry: true,
      });
    }
    throw new ApiRequestError(
      response.status,
      path,
      `${method} ${path} failed: ${response.status} ${detail}`.trim(),
    );
  }

  if (method !== "GET") readCacheGeneration += 1;
  if (response.status === 204) return undefined as TResponse;
  return (await response.json()) as TResponse;
}

/** Authenticated text fetch for immutable extension distribution assets. */
async function requestText(
  path: string,
  query: Record<string, string> = {},
): Promise<string> {
  const url = buildUrl(path, query);
  if (isUiFixtureMode) {
    const { requestUiFixture } = await import("@/lib/ui-fixtures");
    const fixture = await requestUiFixture("GET", url);
    if (fixture.status >= 400) {
      throw new ApiRequestError(
        fixture.status,
        path,
        `GET ${path} failed: ${fixture.status}`,
      );
    }
    return typeof fixture.body === "string" ? fixture.body : "";
  }
  const headers: Record<string, string> = {};
  const fallback = devBearerToken();
  if (fallback) headers.Authorization = `Bearer ${fallback}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new ApiRequestError(
      response.status,
      path,
      `GET ${path} failed: ${response.status} ${detail}`.trim(),
    );
  }
  return response.text();
}

interface MultipartRequestOptions {
  query?: Record<string, string | string[] | undefined>;
  formData: FormData;
  /** Internal: never refresh-and-retry on a CSRF 401 (no loops). */
  skipCsrfRetry?: boolean;
}

/** Multipart sibling of `request()`: sends `FormData` with the same cookie,
 * CSRF header, fixture interception, and CSRF-refresh retry. The browser sets
 * the multipart boundary itself, so Content-Type is never set manually. */
async function requestMultipart<TResponse>(
  method: "POST" | "PUT",
  path: string,
  options: MultipartRequestOptions,
): Promise<TResponse> {
  const url = buildUrl(path, options.query);

  if (isUiFixtureMode) {
    const { requestUiFixture } = await import("@/lib/ui-fixtures");
    const fixture = await requestUiFixture(method, url, options.formData);
    if (fixture.status >= 400) {
      throw new ApiRequestError(
        fixture.status,
        path,
        `${method} ${path} failed: ${fixture.status}`,
      );
    }
    return fixture.body as TResponse;
  }

  const headers: Record<string, string> = {};
  // Cookie sessions require a CSRF token on mutations (see `request()`).
  const csrf = getStoredCsrfToken();
  if (csrf) headers["x-csrf-token"] = csrf;
  const fallback = devBearerToken();
  if (fallback) headers.Authorization = `Bearer ${fallback}`;

  const response = await fetch(url, {
    method,
    credentials: "include",
    headers,
    body: options.formData,
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    if (
      response.status === 401 &&
      !options.skipCsrfRetry &&
      !devBearerToken() &&
      /csrf/i.test(detail)
    ) {
      await refreshCsrfToken();
      return requestMultipart<TResponse>(method, path, {
        ...options,
        skipCsrfRetry: true,
      });
    }
    throw new ApiRequestError(
      response.status,
      path,
      `${method} ${path} failed: ${response.status} ${detail}`.trim(),
    );
  }

  readCacheGeneration += 1;
  if (response.status === 204) return undefined as TResponse;
  return (await response.json()) as TResponse;
}

// --- Auth (/auth/*, /user) --------------------------------------------

export interface CurrentUser {
  id: string;
  email: string;
  username: string;
}

export async function logout(): Promise<void> {
  try {
    await request("POST", "/auth/logout");
  } finally {
    clearStoredCsrfToken();
    invalidateReadCache();
  }
}

/** Restores session state on page load. Throws `ApiRequestError(401)` if signed out. */
export function currentUser(): Promise<CurrentUser> {
  return request("GET", "/user");
}

// --- Organizations + legal entities -----------------------------------------
// The tenancy spine from docs/engineering/TENANCY_MODEL.md: organization is
// the authorization root, every crate row is scoped to it, and each
// organization has at least one primary legal entity whose books it keeps.

export type { OrganizationResponse };

export function listOrganizations(): Promise<OrganizationResponse[]> {
  return request("GET", "/organizations");
}

export interface ExtensionV2InvokeBody {
  kind: "view" | "ui_command";
  app: "organizations" | "niche-accounting";
  payload: unknown;
}

export interface ExtensionV2Error {
  code: string;
  message: string;
  retryable: boolean;
  details?: Record<string, unknown>;
}

export interface ExtensionV2Response<TResult = unknown> {
  ok: boolean;
  requestId?: string;
  kind?: string;
  result?: TResult;
  error?: ExtensionV2Error;
}

export function invokeExtensionV2<TResult = unknown>(
  organization_id: string,
  installation_id: string,
  body: ExtensionV2InvokeBody,
): Promise<ExtensionV2Response<TResult>> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/v2/invoke`,
    {
      body,
      headers: { "Idempotency-Key": crypto.randomUUID() },
    },
  );
}

/** Read one organization's installations without consulting or changing active scope. */
export function listExtensionInstallations(
  organization_id: string,
): Promise<ExtensionInstallation[]> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations`,
  );
}

// --- Extension source documents, imports, and invocation logs --------------
// The import flow binds one uploaded workbook to one coverage tuple (profile +
// report license + inclusive period). Uploading retains evidence; only the
// explicit import POST runs the installed `import_profile` entrypoint.

export type ExtensionInvocationLogEvent = ExtensionInvocationLog;
export type { ExtensionSourceDocumentMetadata, ExtensionSourceDocumentUploadMetadata };

interface ExtensionSourceCoverage {
  organization_id: string;
  profile: string;
  license_id: string;
  period_start: string;
  period_end: string;
}

/** Latest retained evidence revision for one exact coverage tuple. */
export function getCurrentExtensionSourceDocument(
  organization_id: string,
  installation_id: string,
  coverage: ExtensionSourceCoverage,
): Promise<ExtensionSourceDocumentCurrentResponse> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/source-documents/current`,
    { query: { ...coverage } },
  );
}

/** Read-only dependency import snapshot for the close-review UI. The API
 * verifies the consumer installation's live `records:imports` Read grant and
 * returns the same bounded evidence the close host composes. */
export function getExtensionDependencyImports(
  organization_id: string,
  installation_id: string,
): Promise<{ snapshot: unknown }> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/dependency-imports`,
  );
}

/** Retains one workbook revision. Identical bytes under the same coverage
 * return the existing revision with `duplicate: true`. */
export function uploadExtensionSourceDocument(
  organization_id: string,
  installation_id: string,
  metadata: ExtensionSourceDocumentUploadMetadata,
  file: Blob,
): Promise<ExtensionSourceDocumentUploadResponse> {
  const formData = new FormData();
  formData.append("metadata", JSON.stringify(metadata));
  formData.append("file", file);
  return requestMultipart(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/source-documents`,
    { query: { organization_id }, formData },
  );
}

/** Fetches one immutable extension distribution asset. The asset is inert
 * here; the generic extension page embeds it only inside a sandboxed frame. */
export function getExtensionViewAsset(
  organization_id: string,
  installation_id: string,
  path: string,
): Promise<string> {
  return requestText(
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/view/${path.split("/").map(encodeURIComponent).join("/")}`,
    { organization_id },
  );
}

/** Runs the installed `import_profile` entrypoint for one ready document. */
export function runExtensionImport(
  organization_id: string,
  installation_id: string,
  document_id: string,
): Promise<ExtensionImportResponse> {
  return request(
    "POST",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/imports`,
    { body: { organization_id, document_id } },
  );
}

interface ExtensionInvocationLogFilters {
  event_type?: string;
  trigger?: string;
  status?: "pending" | "running" | "succeeded" | "failed";
  from_date?: string;
  through_date?: string;
}

/** Newest invocation events for one installation, optionally filtered by
 * trigger (for example `import_profile`), status, or event type. */
export function listExtensionInstallationLogs(
  organization_id: string,
  installation_id: string,
  filters: ExtensionInvocationLogFilters = {},
  cursor?: string,
): Promise<ExtensionInvocationLogPage> {
  return request(
    "GET",
    `/organizations/${encodeURIComponent(organization_id)}/build/installations/${encodeURIComponent(installation_id)}/logs`,
    {
      query: {
        organization_id,
        event_type: filters.event_type,
        trigger: filters.trigger,
        status: filters.status,
        from_date: filters.from_date,
        through_date: filters.through_date,
        cursor,
      },
    },
  );
}

// Matches the legal-entity rows returned by the Worker's tenancy routes.
export interface LegalEntity {
  id: string;
  organization_id: string;
  slug: string;
  name: string;
  kind: string;
  given_name?: string | null;
  family_name?: string | null;
  tax_classification?: string | null;
  tax_id_last4?: string | null;
  registration_number?: string | null;
  country_code?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  address_city?: string | null;
  address_region?: string | null;
  address_postal_code?: string | null;
  is_primary: boolean;
  active: boolean;
}

export function listLegalEntities(
  organization_id: string,
): Promise<LegalEntity[]> {
  return request("GET", "/legal-entities", { query: { organization_id } });
}

// --- Locations + report licenses (organization scope) ----------------------
// Extension source uploads bind to a platform report license (`platform_licenses`
// public id) named by its legal holder. Organization-wide license discovery
// includes both premises licenses and holder licenses that have no location.

interface OrganizationLocationSummary {
  id: string;
  organization_id: string;
  code: string;
  name: string;
  active: boolean;
}

export function listOrganizationLocations(
  organization_id: string,
): Promise<OrganizationLocationSummary[]> {
  return request("GET", "/locations", { query: { organization_id } });
}

export type OrganizationLocationLicense = LicenseSummary;

export function listOrganizationLicenses(
  organization_id: string,
): Promise<OrganizationLocationLicense[]> {
  return request(
    "GET",
    `/tenancy/organizations/${encodeURIComponent(organization_id)}/licenses`,
  );
}

export function listLocationLicenses(
  location_id: string,
  organization_id: string,
): Promise<OrganizationLocationLicense[]> {
  return request(
    "GET",
    `/locations/${encodeURIComponent(location_id)}/licenses`,
    { query: { organization_id } },
  );
}

/** Every active report license among the organization's licenses, sorted by number. */
export async function listActiveOrganizationLicenses(
  organization_id: string,
): Promise<OrganizationLocationLicense[]> {
  const licenses = (await listOrganizationLicenses(organization_id))
    .filter((license) => license.status === "active");
  licenses.sort((a, b) => a.license_number.localeCompare(b.license_number));
  return licenses;
}

/** A premises license the selected entity can use for a report upload or close.
 * The caller shows the location, not the license number. */
export interface EntityReportLocation {
  id: string;
  legal_entity_id: string;
  location_id: string;
  location_name: string;
  license_type: string;
  status: string;
}

const REPORTABLE_LICENSE_STATUS = new Set(["active"]);

export function reportLocationLabel(
  row: Pick<EntityReportLocation, "location_id" | "location_name" | "license_type">,
  rows: readonly Pick<EntityReportLocation, "location_id">[],
): string {
  const shared = rows.filter((item) => item.location_id === row.location_id).length > 1;
  return shared ? `${row.location_name} · ${row.license_type}` : row.location_name;
}

export async function listEntityReportLocations(
  organization_id: string,
  legal_entity_id: string,
): Promise<EntityReportLocation[]> {
  const [locations, licenses] = await Promise.all([
    listOrganizationLocations(organization_id),
    listOrganizationLicenses(organization_id),
  ]);
  const names = new Map(
    locations
      .filter((location) => location.active)
      .map((location) => [location.id, location.name]),
  );
  const rows = licenses.flatMap((license): EntityReportLocation[] => {
    if (license.legal_entity_id !== legal_entity_id || !license.location_id) return [];
    if (!REPORTABLE_LICENSE_STATUS.has(license.status)) return [];
    const location_name = names.get(license.location_id);
    if (!location_name) return [];
    return [{
      id: license.id,
      legal_entity_id: license.legal_entity_id,
      location_id: license.location_id,
      location_name,
      license_type: license.license_type,
      status: license.status,
    }];
  });
  rows.sort((left, right) =>
    left.location_name.localeCompare(right.location_name)
    || left.license_type.localeCompare(right.license_type)
    || left.id.localeCompare(right.id));
  return rows;
}

// --- Crate catalog + installations (/crates) ------------------------------

export interface CrateSummary {
  key: string;
  branch_key: string;
  tier: string;
  owner_kind: string;
  partner_key?: string;
  status: string;
  display_name: string;
  summary: string;
  resource_root: string;
  requires_capabilities: string[];
  provides_capabilities: string[];
  installation_state?: string;
  entitlement_state?: string;
  /** "available" when usable now; otherwise an `AccessFailure` code such as
   * "entitlement_required" or "crate_not_installed" — see `crate_access.rs`. */
  access: string;
}

export function listCrates(organization_id: string): Promise<CrateSummary[]> {
  return request("GET", "/crates", { query: { organization_id } });
}

// Installing a crate is separate from being entitled to it (see
// `backend/service/src/controllers/crates.rs`'s install/
// uninstall lifecycle) — a finance surface can be entitled but still fail
// its installation check until this runs once for the organization.
export function installCrate(
  crate_key: string,
  organization_id: string,
): Promise<unknown> {
  return request("POST", `/crates/${crate_key}/installations`, {
    body: { organization_id },
  });
}

// --- Automation (/organizations/{id}/automation) -----------------------
// Per-organization opt-in for the nightly bookkeeping pass. Every flag is
// off until the organization's owner turns it on.

export interface AutomationSettings {
  bookkeeping_sweep: boolean;
  recurring_generation: boolean;
  payment_reminders: boolean;
  stripe_retry: boolean;
  /** UTC date of the last nightly pass that finished, if any. */
  last_run_on: string | null;
}

export function getAutomationSettings(
  organization_id: string,
): Promise<AutomationSettings> {
  return request("GET", `/organizations/${organization_id}/automation`);
}

export function updateAutomationSettings(
  organization_id: string,
  changes: Partial<Omit<AutomationSettings, "last_run_on">>,
): Promise<AutomationSettings> {
  return request("PUT", `/organizations/${organization_id}/automation`, {
    body: changes,
  });
}

// --- Chart of accounts (/finance/invoicing/chart-of-accounts) ---------
// Matches CreateChartAccountRequest / ChartAccountResponse in
// crate_surfaces.rs, not the generated (stale) client types — see header.

export interface ChartAccount {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  code: string;
  name: string;
  account_type: string;
  /** Optional reporting sub-classification (e.g. `cash`, `bank`). */
  sub_type?: string;
  /** True for accounts the platform owns; they cannot be re-coded,
   * renamed, or deactivated. */
  is_system: boolean;
  parent_account_id?: string;
  active: boolean;
}

interface CreateChartAccountInput {
  code: string;
  name: string;
  account_type: string;
  sub_type?: string;
  parent_account_id?: string;
}

interface UpdateChartAccountInput {
  code?: string;
  name?: string;
  sub_type?: string;
  parent_account_id?: string;
  active?: boolean;
}

/** Echo of a chart-account lifecycle action (`FinanceCreatedResourceResponse`). */
interface ChartAccountActionResult {
  id: string;
  organization_id: string;
}

export function listChartAccounts(): Promise<ChartAccount[]> {
  return request("GET", "/finance/invoicing/chart-of-accounts", {
    query: currentOrganizationScope(),
  });
}

export function getChartAccount(id: string): Promise<ChartAccount> {
  return request(
    "GET",
    `/finance/invoicing/chart-of-accounts/${encodeURIComponent(id)}`,
    { query: currentOrganizationScope() },
  );
}

export function updateChartAccount(
  id: string,
  patch: UpdateChartAccountInput,
): Promise<ChartAccount> {
  return request(
    "PATCH",
    `/finance/invoicing/chart-of-accounts/${encodeURIComponent(id)}`,
    { query: currentOrganizationScope(), body: patch },
  );
}

function chartAccountLifecycle(
  id: string,
  action: "deactivate" | "reactivate",
): Promise<ChartAccountActionResult> {
  const { organization_id } = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/chart-of-accounts/${encodeURIComponent(id)}/${action}`,
    { body: { organization_id } },
  );
}

export function deactivateChartAccount(
  id: string,
): Promise<ChartAccountActionResult> {
  return chartAccountLifecycle(id, "deactivate");
}

export function reactivateChartAccount(
  id: string,
): Promise<ChartAccountActionResult> {
  return chartAccountLifecycle(id, "reactivate");
}

export function createChartAccount(
  input: CreateChartAccountInput,
): Promise<{ id: string; organization_id: string; legal_entity_id: string }> {
  return request("POST", "/finance/invoicing/chart-of-accounts", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// --- Journal lines, read-only (/finance/accounting/journal-lines) -----
// Read-only view of posted lines; manual entries are created via `createJournalEntry` (POST /finance/invoicing/journal-entries) below.

/** Explicit, permission-checked and audited setup. Installing the Accounting
 * crate does not register its host. Never call this from a read or effect. */
export function enableAccountingHost(organization_id: string): Promise<{ organization_id: string; partner_key: string }> {
  return request("POST", "/finance/accounting/hosts", { body: { organization_id } });
}

/** Current organization's Accounting host, or null before explicit setup. */
export function getAccountingHost(): Promise<{
  organization_id: string;
  partner_key: string;
  enabled_by: string;
  enabled_at: string;
} | null> {
  return request("GET", "/finance/accounting/hosts", {
    query: currentOrganizationScope(),
  });
}

export type JournalLine = AccountingJournalLineResponse & {
  legal_entity_id?: string;
  location_id?: string | null;
};

/** At most 500 posted journal lines for the active books in chronological
 * order. A full-sized response may be incomplete; it is not an account balance.
 * Pass `accountId` to narrow to one chart account. */
export function listJournalLines(accountId?: string): Promise<JournalLine[]> {
  return request("GET", "/finance/accounting/journal-lines", {
    query: {
      ...currentOrganizationScope(),
      ...(accountId ? { account_id: accountId } : {}),
    },
  });
}

// --- Invoices (/finance/invoicing/invoices) ----------------------------
// Matches CreateInvoiceRequest / InvoiceResponse in crate_surfaces.rs.

export interface Invoice {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  sales_order_id?: string;
  invoice_number: string;
  customer_name: string;
  status: string;
  total_minor: number;
  due_at: string;
  issue_date?: string | null;
  payment_term_id?: string | null;
  crm_contact_id?: string;
  /** The counterparty legal entity the invoice is against. */
  counterparty_legal_entity_id?: string;
  /** Integer minor units paid against the invoice so far. */
  amount_paid_cents: number;
  /** Remaining balance (total − paid) in integer minor units. */
  balance_due_cents: number;
  /** Whole days past due; 0 means due today or not yet due. */
  days_overdue: number;
  /** ISO 4217 currency for the amounts on this invoice. */
  currency_code: string;
  // Forward-compat enrichment kept optional; the four enriched fields above
  // are the frozen worklist contract.
  amount_due_cents?: number;
  overdue_interest_cents?: number;
}

export interface CreateInvoiceLineInput {
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
}

export type InvoiceLineUnit =
  | "count"
  | "gram"
  | "kilogram"
  | "ounce"
  | "pound"
  | { custom: string };

interface CreateInvoiceInput {
  sales_order_id?: string;
  customer_name: string;
  due_at?: string;
  issue_date?: string;
  payment_term_id?: string | null;
  crm_contact_id?: string;
  counterparty_legal_entity_id?: string;
  lines: CreateInvoiceLineInput[];
}

/** One aging bucket in a worklist summary. Labels match the aged-report
 * buckets ("Current", "1-30 days", …) so the aging bar lines up with the
 * AgedTables surfaces. */
export interface WorklistAgingBucket {
  label: string;
  total_cents: number;
  count: number;
}

export interface InvoiceSummary {
  total_count: number;
  open_count: number;
  outstanding_cents: number;
  overdue_cents: number;
  aging: WorklistAgingBucket[];
}

export interface BillStatusCount {
  status: string;
  count: number;
  total_cents: number;
}

export interface BillCounts {
  total_count: number;
  open_count: number;
  outstanding_cents: number;
  overdue_cents: number;
  aging: WorklistAgingBucket[];
  by_status: BillStatusCount[];
}

export function listInvoices(): Promise<Invoice[]> {
  return request("GET", "/finance/invoicing/invoices", {
    query: currentOrganizationScope(),
  });
}

/** Receivables worklist rollup for the active books scope. */
export function getInvoiceSummary(
  scope: OrganizationScope = currentOrganizationScope(),
): Promise<InvoiceSummary> {
  return request("GET", "/finance/invoicing/invoices/summary", {
    query: scope,
  });
}

/** Move an invoice to a new status through the transition route. */
export function transitionInvoice(
  invoice_id: string,
  status: string,
): Promise<{ id: string; status: string }> {
  return request(
    "POST",
    `/finance/invoicing/invoices/${invoice_id}/transition`,
    { body: { ...currentOrganizationScope(), status } },
  );
}

export function createInvoice(input: CreateInvoiceInput): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  invoice_number: string;
  total_minor: number;
}> {
  return request("POST", "/finance/invoicing/invoices", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// --- Invoice portal tokens (POST /finance/invoicing/invoices/{id}/portal-tokens) ---
// Mints a token-scoped public link for one invoice. The raw token is
// returned exactly once (see `create_invoice_portal_token` in
// crate_surfaces.rs); the link itself is view-only against the public
// `GET /portal/{token}/invoice` route in
// `service/src/controllers/portal.rs`. This app only mints + copies the
// link — there is no customer-facing portal UI here.

interface InvoicePortalToken {
  id: string;
  token: string;
  expires_at: string;
}

export function createInvoicePortalToken(
  invoice_id: string,
  input: { ttl_days?: number } = {},
): Promise<InvoicePortalToken> {
  return request(
    "POST",
    `/finance/invoicing/invoices/${invoice_id}/portal-tokens`,
    {
      body: { ...currentOrganizationScope(), ...input },
    },
  );
}

/** Public view-only URL for a minted portal token (link affordance only). */
export function portalInvoiceUrl(token: string): string {
  const origin = API_ORIGIN || window.location.origin;
  return `${origin}/portal/${token}/invoice`;
}

// --- Quotes / estimates (/finance/invoicing/quotes) -------------------
// Quotes are priced offers, not ledger documents. They share invoice-shaped
// lines, but only an accepted quote can be converted into a draft invoice.
// The local shapes deliberately follow the service contract while generated
// TypeSpec output catches up with this new resource.

export interface QuoteLine {
  id: string;
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  tax_cents: number;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
  sort_order: number;
}

export interface Quote {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  quote_number: string;
  customer_name: string;
  crm_contact_id?: string;
  status:
    | "draft"
    | "sent"
    | "accepted"
    | "declined"
    | "expired"
    | "converted"
    | string;
  total_minor: number;
  issue_date: string;
  expiry_date: string;
  reference?: string;
  notes?: string;
  currency_code?: string;
  converted_invoice_id?: string;
  sent_at?: string;
  responded_at?: string;
  lines?: QuoteLine[];
}

export interface CreateQuoteLineInput {
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
}

interface CreateQuoteInput {
  customer_name: string;
  issue_date: string;
  expiry_date: string;
  crm_contact_id?: string;
  reference?: string;
  notes?: string;
  lines: CreateQuoteLineInput[];
}

type UpdateQuoteInput = CreateQuoteInput;

export function listQuotes(): Promise<Quote[]> {
  return request("GET", "/finance/invoicing/quotes", {
    query: currentOrganizationScope(),
  });
}

export function getQuote(quote_id: string): Promise<Quote> {
  return request("GET", `/finance/invoicing/quotes/${quote_id}`, {
    query: currentOrganizationScope(),
  });
}

export function createQuote(input: CreateQuoteInput): Promise<Quote> {
  return request("POST", "/finance/invoicing/quotes", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function updateQuote(
  quote_id: string,
  input: UpdateQuoteInput,
): Promise<Quote> {
  return request("PUT", `/finance/invoicing/quotes/${quote_id}`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

function quoteAction(
  quote_id: string,
  action: "send" | "accept" | "decline",
): Promise<Quote> {
  return request("POST", `/finance/invoicing/quotes/${quote_id}/${action}`, {
    body: currentOrganizationScope(),
  });
}

export function sendQuote(quote_id: string): Promise<Quote> {
  return quoteAction(quote_id, "send");
}

export function acceptQuote(quote_id: string): Promise<Quote> {
  return quoteAction(quote_id, "accept");
}

export function declineQuote(quote_id: string): Promise<Quote> {
  return quoteAction(quote_id, "decline");
}

interface ConvertQuoteInput {
  /** Required by the conversion route; ISO timestamp for the new invoice. */
  due_at: string;
}

interface ConvertQuoteResult {
  invoice_id: string;
}

export function convertQuote(
  quote_id: string,
  input: ConvertQuoteInput,
): Promise<ConvertQuoteResult> {
  return request("POST", `/finance/invoicing/quotes/${quote_id}/convert`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

interface QuotePortalToken {
  id: string;
  token: string;
  expires_at: string;
}

export function createQuotePortalToken(
  quote_id: string,
  input: { ttl_days?: number } = {},
): Promise<QuotePortalToken> {
  return request(
    "POST",
    `/finance/invoicing/quotes/${quote_id}/portal-tokens`,
    { body: { ...currentOrganizationScope(), ...input } },
  );
}

/** Public customer URL for a minted, quote-scoped portal token. */
export function portalQuoteUrl(token: string): string {
  const origin = API_ORIGIN || window.location.origin;
  return `${origin}/portal/${token}/quote`;
}

// --- Bills (/finance/invoicing/bills) ---------------------------------
// Matches CreateBillRequest / BillResponse in crate_surfaces.rs. Amounts are
// integer minor units (cents) — never floats.

export interface Bill {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  bill_number: string;
  supplier_name: string;
  status: string;
  total_cents: number;
  due_at: string;
  issue_date?: string | null;
  payment_term_id?: string | null;
  crm_contact_id?: string;
  /** The counterparty legal entity the bill is against. */
  counterparty_legal_entity_id?: string;
  /** Integer minor units paid against the bill so far. */
  amount_paid_cents: number;
  /** Remaining balance (total − paid) in integer minor units. */
  balance_due_cents: number;
  /** Whole days past due; 0 means due today or not yet due. */
  days_overdue: number;
  /** ISO 4217 currency for the amounts on this bill. */
  currency_code: string;
}

export interface CreateBillLineInput {
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
}

interface CreateBillInput {
  supplier_name: string;
  due_at?: string;
  issue_date?: string;
  payment_term_id?: string | null;
  crm_contact_id?: string;
  lines: CreateBillLineInput[];
}

export function listBills(params: { status?: string } = {}): Promise<Bill[]> {
  return request("GET", "/finance/invoicing/bills", {
    query: {
      ...currentOrganizationScope(),
      ...(params.status ? { status: params.status } : {}),
    },
  });
}

export function createBill(input: CreateBillInput): Promise<Bill> {
  return request("POST", "/finance/invoicing/bills", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

/** Payables worklist rollup (aging + per-status counts) for the active scope. */
export function getBillCounts(
  scope: OrganizationScope = currentOrganizationScope(),
): Promise<BillCounts> {
  return request("GET", "/finance/invoicing/bills/counts", {
    query: scope,
  });
}

interface RecordBillPaymentInput {
  amount_minor: number;
  method: string;
  received_at: string;
  /** Optional bank/card account; defaults to the books' bank account. */
  bank_account_id?: string;
}

export function payBill(
  bill_id: string,
  input: RecordBillPaymentInput,
): Promise<{
  id: string;
  organization_id: string;
  bill_id: string;
  amount_cents: number;
  method: string;
  status: string;
}> {
  return request("POST", `/finance/invoicing/bills/${bill_id}/pay`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function transitionBill(
  bill_id: string,
  status: string,
): Promise<{ id: string; status: string }> {
  return request("POST", `/finance/invoicing/bills/${bill_id}/transition`, {
    body: { ...currentOrganizationScope(), status },
  });
}

// --- Payments (/finance/invoicing/payments + /bill-payments) -----------
// Invoice payments (`received` half) and bill payments (`made` half) are
// separate resources — see `list_payments` / `list_bill_payments` in
// crate_surfaces.rs for the split-model note.

export interface InvoicePayment {
  id: string;
  organization_id: string;
  invoice_id: string | null;
  legal_entity_id: string;
  crm_contact_id?: string | null;
  amount_cents: number;
  method: string;
  received_at: string;
  status: string;
  reconciled_at?: string;
  allocations: PaymentApplication[];
  applied_amount_cents: number;
  unapplied_amount_cents: number;
  journal_entry_id?: string;
}

export interface PaymentApplication {
  id: string;
  payment_id?: string;
  invoice_id: string;
  amount_cents: number;
  applied_at?: string;
  status?: string;
}

export interface BillPayment {
  id: string;
  organization_id: string;
  bill_id: string;
  amount_cents: number;
  method: string;
  received_at: string;
  status: string;
  reconciled_at?: string;
  /** Present once the payment is linked to an imported bank transaction. */
  bank_transaction_id?: string;
}

export function listPayments(
  params: { invoice_id?: string; status?: string } = {},
): Promise<InvoicePayment[]> {
  return request("GET", "/finance/invoicing/payments", {
    query: {
      ...currentOrganizationScope(),
      ...(params.invoice_id ? { invoice_id: params.invoice_id } : {}),
      ...(params.status ? { status: params.status } : {}),
    },
  });
}

interface RecordPaymentInput {
  legal_entity_id?: string;
  crm_contact_id?: string;
  allocations?: Array<{ invoice_id: string; amount_minor: number }>;
  amount_minor: number;
  method: string;
  received_at: string;
  idempotency_key?: string;
  /** Optional bank/cash account; defaults to the books' bank account. */
  bank_account_id?: string;
}

export function recordPayment(
  input: RecordPaymentInput,
): Promise<InvoicePayment> {
  return request("POST", "/finance/invoicing/payments", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getPayment(payment_id: string): Promise<InvoicePayment> {
  return request("GET", `/finance/invoicing/payments/${payment_id}`, {
    query: currentOrganizationScope(),
  });
}

export function applyPayment(
  payment_id: string,
  input: {
    invoice_id: string;
    amount_minor: number;
    idempotency_key: string;
  },
): Promise<PaymentApplication> {
  return request(
    "POST",
    `/finance/invoicing/payments/${payment_id}/applications`,
    { body: { ...currentOrganizationScope(), ...input } },
  );
}

export function reconcilePayment(
  payment_id: string,
  input: { reconciled_at?: string } = {},
): Promise<{ id: string; status: string; reconciled_at: string }> {
  return request(
    "POST",
    `/finance/invoicing/payments/${payment_id}/reconcile`,
    {
      body: { ...currentOrganizationScope(), ...input },
    },
  );
}

export function listBillPayments(
  params: { bill_id?: string; status?: string } = {},
): Promise<BillPayment[]> {
  return request("GET", "/finance/invoicing/bill-payments", {
    query: {
      ...currentOrganizationScope(),
      ...(params.bill_id ? { bill_id: params.bill_id } : {}),
      ...(params.status ? { status: params.status } : {}),
    },
  });
}

export function reconcileBillPayment(
  payment_id: string,
  input: { reconciled_at?: string } = {},
): Promise<{ id: string; status: string; reconciled_at: string }> {
  return request(
    "POST",
    `/finance/invoicing/bill-payments/${payment_id}/reconcile`,
    { body: { ...currentOrganizationScope(), ...input } },
  );
}

/**
 * Link one recorded bill payment to an imported bank transaction.
 *
 *   POST /finance/invoicing/bill-payments/{payment_id}/match
 *   body: { organization_id, bank_transaction_id }
 *   response: the updated bill payment, status "reconciled", with
 *             bank_transaction_id set
 *
 * The Payments page only calls it when the reviewer picks a bank line, so
 * the existing no-line reconcile flow still works.
 */
export function matchBillPaymentToBankTransaction(
  payment_id: string,
  input: { bank_transaction_id: string },
): Promise<BillPayment> {
  return request(
    "POST",
    `/finance/invoicing/bill-payments/${payment_id}/match`,
    {
      body: {
        organization_id: currentOrganizationScope().organization_id,
        ...input,
      },
    },
  );
}

// --- Manual journal entries (/finance/invoicing/journal-entries) ------
// Distinct from the read-only `/finance/accounting/journal-lines` view
// above: these are manual entries with a required start/end range on list.
// A locked accounting period rejects the POST with a 409 (`locked_period`
// via `journal_write_error` in crate_surfaces.rs) — callers should surface
// `ApiRequestError.status === 409` explicitly.

export interface JournalEntryLine {
  account_id: string;
  debit_cents: number;
  credit_cents: number;
  location_id?: string | null;
  area_id?: string;
  counterparty_legal_entity_id?: string;
}

export interface JournalEntry {
  posting_review_sha256?: string;
  id: string;
  organization_id: string;
  legal_entity_id: string;
  entry_number: string;
  entry_date: string;
  memo: string;
  status: string;
  total_debit_cents: number;
  total_credit_cents: number;
  lines: JournalEntryLine[];
}

export interface CreateJournalEntryLineInput {
  account_id: string;
  debit_cents: number;
  credit_cents: number;
  location_id?: string | null;
  area_id?: string;
  counterparty_legal_entity_id?: string;
}

export interface CreateJournalEntryInput {
  entry_date: string;
  memo: string;
  lines: CreateJournalEntryLineInput[];
}

export function listJournalEntries(params: {
  start_date: string;
  end_date: string;
  /** Drill-through: keep only entries with a line on this chart account. */
  account_id?: string;
  /** Maximum matching entries. The Worker clamps this to 1–500. */
  limit?: number;
}): Promise<JournalEntry[]> {
  const { limit, ...filters } = params;
  return request("GET", "/finance/invoicing/journal-entries", {
    query: {
      ...currentOrganizationScope(),
      ...filters,
      ...(limit === undefined ? {} : { limit: String(limit) }),
    },
  });
}

export function createJournalEntry(
  input: CreateJournalEntryInput,
): Promise<JournalEntry> {
  return request("POST", "/finance/invoicing/journal-entries", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function postJournalEntry(entryId: string, reviewedSha256: string): Promise<JournalEntry> {
  const body: FinanceJournalEntryPostRequest = { ...currentOrganizationScope(), reviewed_sha256: reviewedSha256 };
  return request("POST", `/finance/invoicing/journal-entries/${encodeURIComponent(entryId)}/post`, { body });
}

export type { FinanceConversionBalanceResponse };

/** The conversion journal for these books. A start date refreshes the open-invoice and open-bill totals. */
export function getConversionBalances(
  booksStartDate?: string,
): Promise<FinanceConversionBalanceResponse> {
  return request("GET", "/finance/invoicing/conversion-balances", {
    query: {
      ...currentOrganizationScope(),
      ...(booksStartDate ? { books_start_date: booksStartDate } : {}),
    },
  });
}

/** Replaces the unposted conversion journal. Posting is a separate reviewed journal post. */
export function saveConversionBalances(input: {
  books_start_date: string;
  lines: FinanceConversionLineRequest[];
}): Promise<FinanceConversionBalanceResponse> {
  const body: FinanceConversionBalanceSaveRequest = {
    ...currentOrganizationScope(),
    books_start_date: input.books_start_date,
    lines: input.lines,
  };
  return request("PUT", "/finance/invoicing/conversion-balances", { body });
}

// --- Fiscal years + period lock ------------------------------------------

export interface FiscalYear {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  name: string;
  start_date: string;
  end_date: string;
  is_closed: boolean;
}

export function listFiscalYears(): Promise<FiscalYear[]> {
  return request("GET", "/finance/invoicing/fiscal-years", {
    query: currentOrganizationScope(),
  });
}

export function createFiscalYear(input: {
  name: string;
  start_date: string;
  end_date: string;
}): Promise<FiscalYear> {
  return request("POST", "/finance/invoicing/fiscal-years", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function closeFiscalYear(
  fiscal_year_id: string,
): Promise<{ id: string; closed: boolean; closing_entry_id?: string }> {
  return request(
    "POST",
    `/finance/invoicing/fiscal-years/${fiscal_year_id}/close`,
    { body: currentOrganizationScope() },
  );
}

export function reopenFiscalYear(
  fiscal_year_id: string,
): Promise<{ id: string; closed: boolean }> {
  return request(
    "POST",
    `/finance/invoicing/fiscal-years/${fiscal_year_id}/reopen`,
    { body: currentOrganizationScope() },
  );
}

export interface PeriodLock {
  locked: boolean;
  lock_date?: string;
  advisor_lock_date?: string;
}

interface PeriodLockPending {
  code: "close_in_progress";
  message: string;
  locked: false;
}

type SetPeriodLockResult = PeriodLock | PeriodLockPending;

export function getPeriodLock(): Promise<PeriodLock> {
  return request("GET", "/finance/invoicing/period-lock", {
    query: currentOrganizationScope(),
  });
}

export function setPeriodLock(input: {
  lock_date: string;
  advisor_lock_date?: string;
  extension_close?: {
    license_id: string;
    period_start: string;
    close_inputs: Record<string, unknown>;
  };
  close_review?: {
    license_id: string;
    period_start: string;
  };
}): Promise<SetPeriodLockResult> {
  return request("PUT", "/finance/invoicing/period-lock", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function clearPeriodLock(): Promise<PeriodLock> {
  return request("DELETE", "/finance/invoicing/period-lock", {
    query: currentOrganizationScope(),
  });
}

export type CloseReviewState =
  | "review_saved"
  | "posting"
  | "posted"
  | "failed"
  | "locked"
  | "correction_required";

export type CloseReviewApprovalState =
  | "not_required"
  | "pending"
  | "approved"
  | "rejected";

export type CloseReviewCorrectionKind = "true_up" | "reopen";

export interface CloseReview {
  review_id: string;
  installation_id: string;
  license_id: string;
  period_start: string;
  period_end: string;
  state: CloseReviewState;
  reviewed_at: string;
  posted_at?: string | null;
  last_error?: string | null;
  reviewed_by_user_id?: string;
  review_revision?: number;
  input_sha256?: string | null;
  approval_required?: boolean;
  approval_reasons?: string[];
  approval_state?: CloseReviewApprovalState;
  approved_by_user_id?: string | null;
  approved_at?: string | null;
  approval_note?: string | null;
  approval_input_sha256?: string | null;
  approval_ref?: string | null;
  correction_of_review_id?: string | null;
  correction_kind?: CloseReviewCorrectionKind | null;
  correction_reason?: string | null;
  reopened_at?: string | null;
  reopen_reason?: string | null;
}

export interface CloseReviewPosting {
  posting_id: string;
  installation_id: string;
  family: string;
  entry_date: string;
  memo: string;
  post_mode: "draft" | "post";
  status: string;
  journal_entry_id?: string | null;
  reversal_journal_entry_id?: string | null;
  reversal_reason?: string | null;
}

export interface CloseReviewList {
  reviews: CloseReview[];
  postings: CloseReviewPosting[];
}

export function getCloseReviews(input: {
  legal_entity_id: string;
  license_id: string;
  period_start: string;
  period_end: string;
}): Promise<CloseReviewList> {
  return request("GET", "/finance/invoicing/close-reviews", {
    query: { ...currentOrganizationScope(), ...input },
  });
}

export interface CloseReviewDetail {
  review: CloseReview;
  form_values: Record<string, string>;
}

export function getCloseReview(reviewId: string): Promise<CloseReviewDetail> {
  return request("GET", `/finance/invoicing/close-reviews/${reviewId}`, {
    query: currentOrganizationScope(),
  });
}

export interface CloseReviewHistory {
  reviews: CloseReview[];
  next_cursor?: string | null;
}

export function getCloseReviewHistory(input: {
  legal_entity_id: string;
  license_id: string;
  limit?: number;
  cursor?: string;
}): Promise<CloseReviewHistory> {
  return request("GET", "/finance/invoicing/close-reviews/history", {
    query: {
      ...currentOrganizationScope(),
      ...input,
      limit: input.limit === undefined ? undefined : String(input.limit),
    },
  });
}

export interface SaveCloseReviewInput {
  legal_entity_id: string;
  installation_id: string;
  license_id: string;
  period_start: string;
  period_end: string;
  close_inputs: Record<string, unknown>;
  form_values?: Record<string, string>;
  approval_required?: boolean;
  approval_reasons?: string[];
  correction_of_review_id?: string;
  correction_kind?: CloseReviewCorrectionKind;
  correction_reason?: string;
}

export function saveCloseReview(
  input: SaveCloseReviewInput,
): Promise<{ review: CloseReview }> {
  return request("PUT", "/finance/invoicing/close-reviews", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function approveCloseReview(
  reviewId: string,
  input: { note?: string; expected_input_sha256: string },
): Promise<{ review: CloseReview }> {
  return request("POST", `/finance/invoicing/close-reviews/${reviewId}/approve`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function returnCloseReview(
  reviewId: string,
  input: { note: string; expected_input_sha256?: string },
): Promise<{ review: CloseReview }> {
  return request("POST", `/finance/invoicing/close-reviews/${reviewId}/return`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

/**
 * Mark a posted/locked review correction-required. This endpoint never moves
 * the period lock; use `reopenPeriodLock` for that.
 */
export function reopenCloseReview(
  reviewId: string,
  input: { reason: string },
): Promise<{ review: CloseReview; lock: PeriodLock }> {
  return request("POST", `/finance/invoicing/close-reviews/${reviewId}/reopen`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function trueUpCloseReview(
  reviewId: string,
  input: {
    target_period_start: string;
    target_period_end: string;
    reason: string;
    approval_ref?: string;
  },
): Promise<{ review: CloseReview }> {
  return request("POST", `/finance/invoicing/close-reviews/${reviewId}/true-up`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function reopenPeriodLock(input: {
  legal_entity_id: string;
  new_lock_date: string;
  reason: string;
}): Promise<PeriodLock> {
  return request("POST", "/finance/invoicing/period-lock/reopen", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export interface CloseReviewPostAccepted {
  code: "posting";
  message: string;
  review_state: "posting";
}

export function postCloseReviews(input: {
  legal_entity_id: string;
  license_id: string;
  period_start: string;
  period_end: string;
}): Promise<CloseReviewList | CloseReviewPostAccepted> {
  return request("POST", "/finance/invoicing/close-reviews/post", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function reverseClosePosting(
  posting_id: string,
  input: {
    legal_entity_id: string;
    reversal_date: string;
    reason: string;
  },
): Promise<CloseReviewList> {
  return request("POST", `/finance/invoicing/close-reviews/${posting_id}/reverse`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// --- Aged receivables / payables -----------------------------------------

export interface AgedReceivableRow {
  id: string;
  invoice_number: string;
  customer_name: string;
  due_date: string;
  amount_due_cents: number;
  days_overdue: number;
}

export interface AgedPayableRow {
  id: string;
  bill_number: string;
  supplier_name: string;
  due_date: string;
  amount_due_cents: number;
  days_overdue: number;
}

export interface AgedBucket<TRow> {
  label: string;
  total_cents: number;
  count: number;
  // Receivables buckets carry `invoices`, payables carry `bills` — one of
  // the two is always present.
  invoices?: TRow[];
  bills?: TRow[];
}

export interface AgedReport<TRow> {
  as_of: string;
  buckets: Array<AgedBucket<TRow>>;
  grand_total_cents: number;
}

export function getAgedReceivables(
  params: { as_of?: string } = {},
): Promise<AgedReport<AgedReceivableRow>> {
  return request("GET", "/finance/invoicing/reports/aged-receivables", {
    query: {
      ...currentOrganizationScope(),
      ...(params.as_of ? { as_of: params.as_of } : {}),
    },
  });
}

export function getAgedPayables(
  params: { as_of?: string } = {},
): Promise<AgedReport<AgedPayableRow>> {
  return request("GET", "/finance/invoicing/reports/aged-payables", {
    query: {
      ...currentOrganizationScope(),
      ...(params.as_of ? { as_of: params.as_of } : {}),
    },
  });
}

// --- Cash-flow statement + GL query (JSON) -------------------------------
// JSON-first read surfaces rendered by CashFlowPage and GlQueryPage. All
// amounts are integer minor units (`*_cents`) — never floats. Note the
// `gl_query_json` handler hardcodes `account_types: None`, so there is no
// server-side account-type filter — GlQueryPage filters client-side.

export interface CashFlowStatement {
  start_date: string;
  end_date: string;
  method: string;
  basis: string;
  opening_cash_balance_cents: number;
  operating_activities: {
    net_income_cents: number;
    depreciation_cents: number;
    working_capital_changes: {
      accounts_receivable_cents: number;
      accounts_payable_cents: number;
      inventory_cents: number;
      other_cents: number;
    };
    total_cents: number;
  };
  investing_activities: {
    items: Array<{
      account_id: string;
      code: string;
      name: string;
      amount_cents: number;
    }>;
    total_cents: number;
  };
  financing_activities: {
    loan_payments_cents: number;
    equity_changes_cents: number;
    items: Array<{
      account_id: string;
      code: string;
      name: string;
      amount_cents: number;
    }>;
    total_cents: number;
  };
  net_cash_change_cents: number;
  closing_cash_balance_cents: number;
  reconciliation: {
    computed_net_change_cents: number;
    cash_account_movement_cents: number;
    difference_cents: number;
    balanced: boolean;
  };
}

export function getCashFlow(
  params: {
    start_date?: string;
    end_date?: string;
    method?: "indirect" | "direct";
    basis?: ReportBasis;
  } = {},
): Promise<CashFlowStatement> {
  return request("GET", "/finance/invoicing/reports/cash-flow", {
    query: { ...currentOrganizationScope(), ...params },
  });
}

// --- Inventory valuation (GET /operations/inventory/valuation-report) ---
// Owned by Operations (Inventory), read-only here. This is the live
// specialist-app snapshot (avg-cost + FIFO), distinct from the cash-flow
// statement's "Inventory change" row, which is a GL working-capital delta.
export interface InventoryValuationSku {
  sku_id: string;
  code: string;
  name: string;
  cost_method: string;
  on_hand_units: number;
  valued: boolean;
  average_cost_cents: number;
  average_value_cents: number | null;
  fifo_value_cents: number | null;
  open_layer_count: number;
}

export interface InventoryValuationReport {
  organization_id: string;
  skus: InventoryValuationSku[];
  total_average_value_cents: number;
  total_fifo_value_cents: number;
}

export function getInventoryValuationReport(): Promise<InventoryValuationReport> {
  return request("GET", "/operations/inventory/valuation-report", {
    query: currentOrganizationScope(),
  });
}

export interface GlQueryAccount {
  account_id: string;
  code: string;
  name: string;
  account_type: string;
  sub_type: string;
  debit_cents: number;
  credit_cents: number;
  balance_cents: number;
}

export interface GlQueryResult {
  start_date: string;
  end_date: string;
  basis: string;
  accounts: GlQueryAccount[];
}

export function getGlQuery(params: {
  start_date: string;
  end_date: string;
  basis?: ReportBasis;
  include_empty_accounts?: boolean;
}): Promise<GlQueryResult> {
  const { include_empty_accounts, ...rest } = params;
  return request("GET", "/finance/invoicing/reports/gl-query", {
    query: {
      ...currentOrganizationScope(),
      ...rest,
      ...(include_empty_accounts !== undefined
        ? { include_empty_accounts: String(include_empty_accounts) }
        : {}),
    },
  });
}

// --- Contact directory (/finance/invoicing/contacts) -------------------
// CRM owns contact identity. Accounting lists and creates the narrow person
// record it needs so invoices, bills, and payments can be raised without a
// separate CRM trip.

export type DirectoryContact = FinanceDirectoryContactResponse;

export interface CreateDirectoryContactInput {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
}

export function listContacts(): Promise<DirectoryContact[]> {
  return request("GET", "/finance/invoicing/contacts", {
    query: currentOrganizationScope(),
  });
}

export function createContact(
  input: CreateDirectoryContactInput,
): Promise<DirectoryContact> {
  return request("POST", "/finance/invoicing/contacts", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// --- Counterparty finance profile (/finance/invoicing/contact-finance-profiles) ---
// Read-one-by-id, not a list — a finance profile extends a legal entity
// (the counterparty) that lives outside this API. Matches
// ContactFinanceProfileResponse / ContactFinanceProfileRequest in
// crate_surfaces.rs.

export interface ContactAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

export interface ContactFinanceProfile {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  counterparty_legal_entity_id: string;
  contact_type: string;
  tax_number?: string;
  payment_terms_days: number;
  payment_term_id?: string | null;
  addresses: { billing?: ContactAddress; shipping?: ContactAddress };
  currency_code: string;
  credit_limit_cents?: number;
  default_revenue_account_id?: string;
  default_expense_account_id?: string;
}

interface UpsertContactFinanceProfileInput {
  counterparty_legal_entity_id: string;
  contact_type: string;
  tax_number?: string;
  payment_terms_days: number;
  /** Omitted preserves the current selection; null clears the default. */
  payment_term_id?: string | null;
  billing_address?: ContactAddress;
  shipping_address?: ContactAddress;
  currency_code: string;
  credit_limit_cents?: number;
  default_revenue_account_id?: string;
  default_expense_account_id?: string;
}

export function readContactFinanceProfile(
  counterpartyLegalEntityId: string,
): Promise<ContactFinanceProfile> {
  return request("GET", "/finance/invoicing/contact-finance-profiles", {
    query: {
      ...currentOrganizationScope(),
      counterparty_legal_entity_id: counterpartyLegalEntityId,
    },
  });
}

export function upsertContactFinanceProfile(
  input: UpsertContactFinanceProfileInput,
): Promise<{ legal_entity_id: string; counterparty_legal_entity_id: string }> {
  return request("PUT", "/finance/invoicing/contact-finance-profiles", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// --- Per-books payment terms (/finance/invoicing/payment-terms) ---------
export type PaymentTermKind = "standard" | "date_driven";
export type PaymentTerm = FinancePaymentTermResponse;
export type CreatePaymentTermInput = Omit<
  FinancePaymentTermRequest,
  "organization_id" | "legal_entity_id"
>;

export function listPaymentTerms(): Promise<PaymentTerm[]> {
  return request("GET", "/finance/invoicing/payment-terms", {
    query: currentOrganizationScope(),
  });
}

export function createPaymentTerm(
  input: CreatePaymentTermInput,
): Promise<PaymentTerm> {
  return request("POST", "/finance/invoicing/payment-terms", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function deactivatePaymentTerm(
  payment_term_id: string,
): Promise<{ id: string; active: boolean }> {
  return request(
    "POST",
    `/finance/invoicing/payment-terms/${payment_term_id}/deactivate`,
    { body: currentOrganizationScope() },
  );
}

// --- Financial statements (read-only) ------------------------------------
// GET /finance/invoicing/reports/trial-balance, /balance-sheet,
// /profit-and-loss. Response types come straight from `@api-client` — unlike
// the write endpoints above, the generated `FinanceStatement*` shapes were
// verified field-for-field against `statement_json()` in
// backend/service/src/controllers/crate_surfaces.rs
// (`amount_cents`/`amounts_cents`, the trial-balance-only `debit_cents`/
// `credit_cents` + `debits_cents`/`credits_cents` legs, section
// `subtotal_cents`/`subtotals_cents` + `debit_total_cents`/
// `credit_total_cents` + `debit_totals_cents`/`credit_totals_cents`, and
// `grand_total_cents`/`grand_totals_cents`) and match. All amounts are
// integer minor units (cents) — never floats.

export type {
  FinanceStatementResponse,
  FinanceStatementRow,
} from "@api-client";

export type ReportBasis = "accrual" | "cash";

interface AsAtReportParams {
  /** Canonical as-at date (YYYY-MM-DD). */
  as_at?: string;
  /** Optional comparison date(s); repeatable on the wire. A single string
   * sends one `compare_date`, an array repeats the param. */
  compare_date?: string | string[];
  basis?: ReportBasis;
  currency?: string;
}

export function getTrialBalance(
  params: AsAtReportParams = {},
): Promise<FinanceStatementResponse> {
  return request("GET", "/finance/invoicing/reports/trial-balance", {
    query: { ...currentOrganizationScope(), ...params },
  });
}

export function getBalanceSheet(
  params: AsAtReportParams = {},
): Promise<FinanceStatementResponse> {
  return request("GET", "/finance/invoicing/reports/balance-sheet", {
    query: { ...currentOrganizationScope(), ...params },
  });
}

interface ProfitAndLossParams {
  start_date: string;
  end_date: string;
  basis?: ReportBasis;
  /** Optional comparative period — the backend requires both bounds
   * together (400 on a half-supplied pair), so both or neither. */
  compare_from?: string;
  compare_to?: string;
  currency?: string;
}

export function getProfitAndLoss(
  params: ProfitAndLossParams,
): Promise<FinanceStatementResponse> {
  return request("GET", "/finance/invoicing/reports/profit-and-loss", {
    query: { ...currentOrganizationScope(), ...params },
  });
}

// --- Phase 5 Books Advanced ------------------------------------------------
// Accrual schedules, repeating journal templates ("Repeating entries" in
// plain-language UI; the technical `recurring_journal` term lives in code +
// tooltips only), budgets, loans, and fixed assets. Shapes below match the
// `*_response` projections in crate_surfaces.rs (see the Phase 5 contract),
// not the generated client — amounts are i64 minor units throughout and
// every handler is scoped by organization + legal entity.

// Accruals: a total split across release periods. Recognizing the next
// unposted period posts DR the release (expense) account / CR the
// accrual-liability account (source_type='accrual'); reversing a posted
// entry posts the exact DR/CR mirror (source_type='reversal'). The entry's
// `posted` flag is the idempotency guard. List/get shapes match
// `accrual_schedule_response` / `accrual_entry_response`.

export interface AccrualEntry {
  id: string;
  period_date: string;
  amount_minor: number;
  journal_entry_id?: string;
  posted: boolean;
  sort_order: number;
}

export interface AccrualSchedule {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  source_entry_id?: string;
  total_minor: number;
  start_date: string;
  end_date: string;
  periods: number;
  account_id: string;
  reverse_account_id: string;
  description: string;
  status: string;
  entries: AccrualEntry[];
}

interface CreateAccrualInput {
  total_minor: number;
  periods: number;
  /** Calendar dates (YYYY-MM-DD). */
  start_date: string;
  end_date: string;
  account_id: string;
  reverse_account_id: string;
  description: string;
  source_entry_id?: string;
}

export function listAccrualSchedules(): Promise<AccrualSchedule[]> {
  return request("GET", "/finance/invoicing/accruals", {
    query: currentOrganizationScope(),
  });
}

export function createAccrualSchedule(input: CreateAccrualInput): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  status: string;
}> {
  return request("POST", "/finance/invoicing/accruals", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getAccrualSchedule(
  schedule_id: string,
): Promise<AccrualSchedule> {
  return request("GET", `/finance/invoicing/accruals/${schedule_id}`, {
    query: currentOrganizationScope(),
  });
}

/** Post the next unposted period. Retries advance to the next period instead
 * of double-posting; a locked period is rejected with a 409. */
export function recognizeAccrualEntry(
  schedule_id: string,
): Promise<{ schedule_id: string; journal_entry_id: string }> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/accruals/${schedule_id}/recognize`,
    {
      body: {
        organization_id: scope.organization_id,
      },
    },
  );
}

/** Reverse one posted entry. Unposted or already-reversed entries are
 * rejected with a 400; a locked reversal date with a 409. */
export function reverseAccrualEntry(
  schedule_id: string,
  entry_id: string,
  input: { reversal_date: string },
): Promise<{
  schedule_id: string;
  entry_id: string;
  reversal_journal_entry_id: string;
}> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/accruals/${schedule_id}/entries/${entry_id}/reverse`,
    {
      body: {
        organization_id: scope.organization_id,
        ...input,
      },
    },
  );
}

// Repeating journal templates: balanced DR/CR line sets posted verbatim on
// each due occurrence (source_type='recurring_journal'). Creation refuses
// unbalanced templates (400); the generate run skips-and-advances any
// unbalanced template that reaches it anyway. List/get shapes match
// `recurring_journal_template_response`. Note the list is scoped by
// organization + legal entity.

export interface RecurringJournalLine {
  id: string;
  description: string;
  account_id: string;
  debit_minor: number;
  credit_minor: number;
  sort_order: number;
}

export interface RecurringJournalTemplate {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  name: string;
  frequency: string;
  start_date: string;
  end_date?: string;
  next_run_date?: string;
  last_run_date?: string;
  occurrences_generated: number;
  max_occurrences?: number;
  status: string;
  reference?: string;
  notes?: string;
  lines: RecurringJournalLine[];
}

interface CreateRecurringJournalLineInput {
  account_id: string;
  description: string;
  debit_minor: number;
  credit_minor: number;
}

interface CreateRecurringJournalInput {
  name: string;
  frequency: string;
  /** Calendar date (YYYY-MM-DD). */
  start_date: string;
  end_date?: string;
  max_occurrences?: number;
  reference?: string;
  notes?: string;
  lines: CreateRecurringJournalLineInput[];
}

export function listRecurringJournalTemplates(): Promise<
  RecurringJournalTemplate[]
> {
  return request("GET", "/finance/invoicing/recurring-journals", {
    query: currentOrganizationScope(),
  });
}

export function createRecurringJournalTemplate(
  input: CreateRecurringJournalInput,
): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  status: string;
}> {
  return request("POST", "/finance/invoicing/recurring-journals", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getRecurringJournalTemplate(
  template_id: string,
): Promise<RecurringJournalTemplate> {
  return request(
    "GET",
    `/finance/invoicing/recurring-journals/${template_id}`,
    { query: currentOrganizationScope() },
  );
}

/** Pause a template (completed templates are rejected with a 400). Paused
 * templates are excluded from the generate run. */
export function pauseRecurringJournalTemplate(
  template_id: string,
): Promise<{ id: string; status: string }> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/recurring-journals/${template_id}/pause`,
    {
      body: {
        organization_id: scope.organization_id,
      },
    },
  );
}

/** Operator-triggered run: posts every due occurrence. Locked-period
 * occurrences are skipped but still advanced; any other error aborts the
 * run. */
export function generateRecurringJournals(
  input: { as_of?: string } = {},
): Promise<{ generated_count: number }> {
  return request("POST", "/finance/invoicing/recurring-journals/generate", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// Budgets: spending plans with per-account lines broken into dated periods.
// Plan-vs-actual reads come from the report endpoint below (flat
// per-period rows); the variances endpoint flags breached thresholds.
// List/get shapes match `budget_response` + the line/period projections in
// `get_budget`. The collection reads are scoped by organization
// only (no legal_entity_id); the reads that compare against actuals take
// the full scope.

export interface Budget {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  name: string;
  fiscal_year_id?: string;
  start_date: string;
  end_date: string;
  period_type: string;
  variance_threshold_pct?: number;
  is_active: boolean;
}

export interface BudgetLine {
  id: string;
  budget_id: string;
  chart_account_id: string;
  total_cents: number;
}

export interface BudgetPeriod {
  id: string;
  budget_line_id: string;
  label: string;
  start_date: string;
  end_date: string;
  amount_cents: number;
  sort_order: number;
}

export interface BudgetDetail {
  budget: Budget;
  lines: BudgetLine[];
  periods_by_line: Array<{ line_id: string; periods: BudgetPeriod[] }>;
}

export interface BudgetVsActualRow {
  budget_id: string;
  budget_line_id: string;
  budget_period_id: string;
  chart_account_id: string;
  period_label: string;
  start_date: string;
  end_date: string;
  budgeted_cents: number;
  actual_cents: number;
}

interface CreateBudgetInput {
  name: string;
  fiscal_year_id?: string;
  /** Calendar dates (YYYY-MM-DD). */
  start_date: string;
  end_date: string;
  period_type: string;
  variance_threshold_pct?: number;
  is_active: boolean;
}

export function listBudgets(): Promise<Budget[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/budgets/budgets", {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

export function createBudget(input: CreateBudgetInput): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
}> {
  return request("POST", "/finance/budgets/budgets", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getBudget(budget_id: string): Promise<BudgetDetail> {
  const scope = currentOrganizationScope();
  return request("GET", `/finance/budgets/budgets/${budget_id}`, {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

export function addBudgetLine(
  budget_id: string,
  input: { chart_account_id: string; total_cents: number },
): Promise<{ id: string; budget_id: string }> {
  return request("POST", `/finance/budgets/budgets/${budget_id}/lines`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function addBudgetPeriod(
  budget_id: string,
  line_id: string,
  input: {
    label: string;
    start_date: string;
    end_date: string;
    amount_cents: number;
    sort_order: number;
  },
): Promise<{ id: string; line_id: string }> {
  return request(
    "POST",
    `/finance/budgets/budgets/${budget_id}/lines/${line_id}/periods`,
    { body: { ...currentOrganizationScope(), ...input } },
  );
}

/** Flat per-period plan-vs-actual rows for one budget. */
export function getBudgetVsActual(
  budget_id: string,
): Promise<BudgetVsActualRow[]> {
  return request("GET", "/finance/budgets/reports/budget-vs-actual", {
    query: { ...currentOrganizationScope(), budget_id },
  });
}

// Loans: amortizing borrowings with a server-computed schedule. Recording
// the periodic payment posts the principal/interest split to the ledger.
// List/get shapes match `loan_response` / `loan_schedule_response`; the
// detail response nests them as `{loan, schedule}`. Collection reads are
// scoped by organization only.

export interface Loan {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  name: string;
  principal_cents: number;
  interest_rate_bp: number;
  term_months: number;
  start_date: string;
  monthly_payment_cents: number;
  status: string;
  principal_account_id: string;
  interest_account_id: string;
  bank_account_id?: string;
}

export interface LoanScheduleEntry {
  id: string;
  loan_id: string;
  period_number: number;
  date: string;
  principal_cents: number;
  interest_cents: number;
  total_payment_cents: number;
  remaining_balance_cents: number;
  posted: boolean;
  sort_order: number;
  journal_entry_id?: string;
}

export interface LoanDetail {
  loan: Loan;
  schedule: LoanScheduleEntry[];
}

interface CreateLoanInput {
  name: string;
  bank_account_id?: string;
  principal_cents: number;
  interest_rate_bp: number;
  term_months: number;
  /** Calendar date (YYYY-MM-DD). */
  start_date: string;
  principal_account_id: string;
  interest_account_id: string;
}

export function listLoans(): Promise<Loan[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/loans/loans", {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

export function createLoan(input: CreateLoanInput): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  schedule_entry_count: number;
}> {
  return request("POST", "/finance/loans/loans", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getLoan(loan_id: string): Promise<LoanDetail> {
  const scope = currentOrganizationScope();
  return request("GET", `/finance/loans/loans/${loan_id}`, {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

/** Record the periodic payment: posts the principal/interest split and
 * advances the schedule. */
export function postLoanPayment(
  loan_id: string,
  input: { date?: string } = {},
): Promise<{
  schedule_entry: LoanScheduleEntry;
  loan_status: string;
}> {
  return request("POST", `/finance/loans/loans/${loan_id}/payments`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// Fixed assets: long-lived purchases written down over time. Categories
// carry the default depreciation settings; assets carry their own (falling
// back to the category's). Shapes match `category_response` /
// `asset_response` (optional fields omitted, never null). Collection reads
// are scoped by organization only.

export interface AssetCategory {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  name: string;
  default_depreciation_method: string;
  default_convention: string;
  default_useful_life_months?: number;
  default_residual_value_cents: number;
  default_depreciation_rate_bp?: number;
  asset_account_id?: string;
  depreciation_account_id?: string;
  accumulated_dep_account_id?: string;
  cwip_account_id?: string;
  is_active: boolean;
}

export interface FixedAsset {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  name: string;
  description?: string;
  asset_number: string;
  category_id?: string;
  purchase_date: string;
  in_service_date?: string;
  purchase_price_cents: number;
  residual_value_cents: number;
  useful_life_months: number;
  depreciation_method: string;
  convention: string;
  total_expected_units?: number;
  unit_of_measure?: string;
  asset_account_id?: string;
  depreciation_account_id?: string;
  accumulated_dep_account_id?: string;
  cwip_account_id?: string;
  accumulated_depreciation_cents: number;
  net_book_value_cents: number;
  is_cwip: boolean;
  capitalized_date?: string;
  revalued_amount_cents?: number;
  revaluation_surplus_balance_cents: number;
  revaluation_reserve_account_id?: string;
  impairment_expense_account_id?: string;
  status: string;
}

interface CreateAssetCategoryInput {
  name: string;
  default_depreciation_method: string;
  default_convention: string;
  default_useful_life_months?: number;
  default_residual_value_cents: number;
  default_depreciation_rate_bp?: number;
  asset_account_id?: string;
  depreciation_account_id?: string;
  accumulated_dep_account_id?: string;
  cwip_account_id?: string;
  is_active?: boolean;
}

interface CreateFixedAssetInput {
  name: string;
  description?: string;
  asset_number: string;
  category_id?: string;
  /** Calendar date (YYYY-MM-DD). */
  purchase_date: string;
  in_service_date?: string;
  purchase_price_cents: number;
  residual_value_cents?: number;
  useful_life_months?: number;
  depreciation_method?: string;
  convention?: string;
  total_expected_units?: number;
  unit_of_measure?: string;
  asset_account_id?: string;
  depreciation_account_id?: string;
  accumulated_dep_account_id?: string;
  cwip_account_id?: string;
  is_cwip?: boolean;
}

export function listAssetCategories(): Promise<AssetCategory[]> {
  return request("GET", "/finance/fixed-assets/categories", {
    query: currentOrganizationScope(),
  });
}

export function createAssetCategory(
  input: CreateAssetCategoryInput,
): Promise<{ id: string; organization_id: string; legal_entity_id: string }> {
  return request("POST", "/finance/fixed-assets/categories", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function listFixedAssets(): Promise<FixedAsset[]> {
  return request("GET", "/finance/fixed-assets/assets", {
    query: currentOrganizationScope(),
  });
}

export function createFixedAsset(
  input: CreateFixedAssetInput,
): Promise<{ id: string; organization_id: string; legal_entity_id: string }> {
  return request("POST", "/finance/fixed-assets/assets", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getFixedAsset(asset_id: string): Promise<FixedAsset> {
  return request("GET", `/finance/fixed-assets/assets/${asset_id}`, {
    query: currentOrganizationScope(),
  });
}

/** Write down one period of wear. Rejected when nothing remains. */
export function depreciateFixedAsset(
  asset_id: string,
  input: { date?: string; units_this_period?: number } = {},
): Promise<{ entry: unknown; asset: FixedAsset }> {
  return request(
    "POST",
    `/finance/fixed-assets/assets/${asset_id}/depreciate`,
    {
      body: { ...currentOrganizationScope(), ...input },
    },
  );
}

export function rollbackFixedAssetDepreciation(
  asset_id: string,
): Promise<{ entry: unknown; asset: FixedAsset }> {
  return request(
    "POST",
    `/finance/fixed-assets/assets/${asset_id}/rollback-depreciation`,
    { body: currentOrganizationScope() },
  );
}

/** Sell or retire the asset. */
export function disposeFixedAsset(
  asset_id: string,
  input: {
    date: string;
    disposal_amount_cents: number;
    proceeds_account_id?: string;
    gain_account_id?: string;
    loss_account_id?: string;
  },
): Promise<FixedAsset> {
  return request("POST", `/finance/fixed-assets/assets/${asset_id}/dispose`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function revalueFixedAsset(
  asset_id: string,
  input: { date: string; revalued_amount_cents: number; notes?: string },
): Promise<{ revaluation: unknown; asset: FixedAsset }> {
  return request("POST", `/finance/fixed-assets/assets/${asset_id}/revalue`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function impairFixedAsset(
  asset_id: string,
  input: { date: string; impairment_amount_cents: number; notes?: string },
): Promise<{ impairment: unknown; asset: FixedAsset }> {
  return request("POST", `/finance/fixed-assets/assets/${asset_id}/impair`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function listCwipCosts(asset_id: string): Promise<
  Array<{
    id: string;
    date: string;
    amount_cents: number;
    description?: string;
  }>
> {
  return request("GET", `/finance/fixed-assets/assets/${asset_id}/cwip-costs`, {
    query: currentOrganizationScope(),
  });
}

export function addCwipCost(
  asset_id: string,
  input: { date: string; amount_cents: number; description?: string },
): Promise<{
  cost: { id: string; amount_cents: number };
  asset: FixedAsset;
}> {
  return request(
    "POST",
    `/finance/fixed-assets/assets/${asset_id}/cwip-costs`,
    { body: { ...currentOrganizationScope(), ...input } },
  );
}

/** Finish building and put the asset into service. */
export function capitalizeFixedAsset(asset_id: string): Promise<
  FixedAsset & {
    capitalization_journal_post?: {
      posted_count: number;
      journal_entry_ids: string[];
    };
  }
> {
  return request(
    "POST",
    `/finance/fixed-assets/assets/${asset_id}/capitalize`,
    { body: currentOrganizationScope() },
  );
}

// --- Procurement GRNI journals (POST /finance/invoicing/procurement-grni-journals/post) ---
// Requisitions, purchase orders, and goods receipts are authored in
// Operations — Accounting never authors them. Posting the goods-received-
// not-invoiced journal to the ledger is Invoicing/Accounting's job, so this
// is the one procurement action that belongs here.
export function postProcurementGrniJournals(): Promise<{
  posted_count: number;
  journal_entry_ids: string[];
}> {
  return request(
    "POST",
    "/finance/invoicing/procurement-grni-journals/post",
    { body: currentOrganizationScope() },
  );
}

// --- Payroll journals (HR prepares the run; Accounting posts the ledger) ---
// GET lists the HR-owned facts for the current legal entity. POST writes the
// ledger journal. Neither call creates an employee or starts a payroll run.
// Response shapes are the generated TypeSpec contracts.
export type PayrollJournalTaxLine = FinancePayrollJournalTaxLine;
export type PayrollJournalReviewEntry = FinancePayrollJournalReviewEntry;
export type PayrollJournalReview = FinancePayrollJournalReviewResponse;

export function listPayrollJournals(): Promise<PayrollJournalReview> {
  return request("GET", "/finance/invoicing/payroll-journals", {
    query: currentOrganizationScope(),
  });
}

export function postPayrollJournals(): Promise<FinancePayrollJournalPostResponse> {
  return request("POST", "/finance/invoicing/payroll-journals/post", {
    body: currentOrganizationScope(),
  });
}

/** Operator-triggered run: writes down every due asset as of one date. */
export function runDepreciation(input: { date?: string } = {}): Promise<{
  processed: number;
  skipped: number;
  results: Array<{ asset_id: string; posted: boolean }>;
}> {
  return request("POST", "/finance/fixed-assets/run-depreciation", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

/**
 * Post every depreciation entry already calculated for this legal entity but
 * not yet written to the ledger. This does not calculate new depreciation —
 * run that first. Returns the posted journal ids so the reviewer can open them.
 */
export function postDepreciationJournals(): Promise<{
  posted_count: number;
  journal_entry_ids: string[];
}> {
  return request("POST", "/finance/invoicing/depreciation-journals/post", {
    body: currentOrganizationScope(),
  });
}

// --- Phase 6 Banking -----------------------------------------------------
// Bank accounts, statement imports, matching rules, scheduled payments, and
// payment batches. Shapes match the `FinanceBank*` / `FinancePaymentBatch*`
// / `FinanceScheduledPayment*` / `FinanceBatchRemittanceResponse` models in
// specs/typespec/branches/finance/invoicing.tsp, not the generated client.
//
// Deliberately absent: single-account detail reads, scheduled-payment and
// payment-batch lists, and transfers between the business's own accounts.
// Bank lines and bank rules have list reads. A manual match accepts a posted
// journal only; open invoices and bills are suggestions, not match targets.

type BankAccountType =
  | "checking"
  | "savings"
  | "cash"
  | "credit_card"
  | "loan"
  | "investment"
  | "other";

export const BANK_ACCOUNT_TYPES: BankAccountType[] = [
  "checking",
  "savings",
  "cash",
  "credit_card",
  "loan",
  "investment",
  "other",
];

export interface BankAccount {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  account_name: string;
  account_type: string;
  chart_account_id?: string;
  balance_cents: number;
  /** The TypeSpec response calls this `active`; tolerate either spelling. */
  active?: boolean;
  is_active?: boolean;
}

/** True unless the backend explicitly marks the account inactive. */
export function bankAccountIsActive(account: BankAccount): boolean {
  return account.is_active ?? account.active ?? true;
}

/** Plain-language account-type label (`credit_card` → `Credit card`). */
export function bankAccountTypeLabel(accountType: string): string {
  const words = accountType.replace(/_/g, " ").trim();
  if (!words) return accountType;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function listBankAccounts(): Promise<BankAccount[]> {
  return request("GET", "/finance/invoicing/bank-accounts", {
    query: currentOrganizationScope(),
  });
}

export function createBankAccount(input: {
  account_name: string;
  account_type: BankAccountType | string;
}): Promise<{ id: string; organization_id: string }> {
  return request("POST", "/finance/invoicing/bank-accounts", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export type LowBalanceAlert = FinanceLowBalanceAlert;

export function getLowBalanceAlerts(): Promise<FinanceLowBalanceAlertListResponse> {
  return request("GET", "/finance/invoicing/bank-accounts/low-balance-alerts", {
    query: currentOrganizationScope(),
  });
}

export type BankTransactionLine = FinanceBankTransactionResponse;

export function listBankTransactions(
  bank_account_id: string,
): Promise<BankTransactionLine[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/bank-transactions", {
    query: {
      organization_id: scope.organization_id,
      bank_account_id,
    },
  });
}

export type BankMatch = FinanceBankTransactionMatch;
export function listBankTransactionMatches(
  transaction_id: string,
): Promise<BankMatch[]> {
  const scope = currentOrganizationScope();
  return request(
    "GET",
    `/finance/invoicing/bank-transactions/${transaction_id}/matches`,
    {
      query: {
        organization_id: scope.organization_id,
      },
    },
  );
}

/** Link one unmatched bank line to a posted journal. Invoice and bill ids are refused. */
export function matchBankTransaction(
  transaction_id: string,
  input: { journal_entry_id: string },
): Promise<FinanceBankTransactionMatchResponse> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/bank-transactions/${transaction_id}/match`,
    {
      body: {
        organization_id: scope.organization_id,
        ...input,
      },
    },
  );
}

// Categorize one imported bank transaction against a ledger account.
export function categorizeBankTransaction(
  transaction_id: string,
  input: { account_id: string },
): Promise<{ id: string; journal_entry_id?: string }> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/bank-transactions/${transaction_id}/categorize`,
    {
      body: {
        organization_id: scope.organization_id,
        ...input,
      },
    },
  );
}

export type BankRuleMatchField = "description" | "reference";
export type BankRuleMatchType =
  | "contains"
  | "equals"
  | "starts_with"
  | "ends_with";

interface CreateBankRuleInput {
  name: string;
  priority: number;
  match_field: BankRuleMatchField;
  match_type: BankRuleMatchType;
  match_value: string;
  account_id?: string;
  auto_reconcile: boolean;
}

export type BankRuleRecord = FinanceBankRuleResponse;

export function listBankRules(): Promise<BankRuleRecord[]> {
  return request("GET", "/finance/invoicing/bank-rules", {
    query: currentOrganizationScope(),
  });
}

export function updateBankRule(
  rule_id: string,
  input: CreateBankRuleInput & { is_active: boolean },
): Promise<FinanceBankRuleUpdateResponse> {
  return request("POST", `/finance/invoicing/bank-rules/${rule_id}`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function createBankRule(
  input: CreateBankRuleInput,
): Promise<{ id: string; organization_id: string }> {
  return request("POST", "/finance/invoicing/bank-rules", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function applyBankRules(
  bank_account_id: string,
): Promise<{ applied: number; reconciled: number }> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/bank-accounts/${bank_account_id}/apply-rules`,
    {
      body: {
        organization_id: scope.organization_id,
      },
    },
  );
}

export interface AccountSuggestion {
  account_id: string;
  account_name: string;
  account_code: string;
  /** Confidence score, 0–100. */
  confidence: number;
  match_count: number;
}

export function suggestAccounts(
  bank_account_id: string,
  description: string,
  limit?: number,
): Promise<AccountSuggestion[]> {
  const scope = currentOrganizationScope();
  return request(
    "GET",
    `/finance/invoicing/bank-accounts/${bank_account_id}/suggest`,
    {
      query: {
        organization_id: scope.organization_id,
        description,
        ...(limit !== undefined ? { limit: String(limit) } : {}),
      },
    },
  );
}

export type StatementReconciliationReview =
  FinanceStatementReconciliationReviewResponse;

type StatementReconciliationFinish =
  FinanceStatementReconciliationFinishResponse;

/** Ask the API whether this statement can finish. The screen does not decide. */
export function reviewStatementReconciliation(
  bank_account_id: string,
  input: {
    statement_ending_date: string;
    statement_ending_balance_cents: number;
  },
): Promise<StatementReconciliationReview> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/bank-accounts/${bank_account_id}/reconciliation-review`,
    {
      body: {
        organization_id: scope.organization_id,
        ...input,
      },
    },
  );
}

/** Save a finished statement. The API refuses unless the review allows it. */
export function finishStatementReconciliation(
  bank_account_id: string,
  input: {
    statement_ending_date: string;
    statement_ending_balance_cents: number;
  },
): Promise<StatementReconciliationFinish> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/bank-accounts/${bank_account_id}/reconciliation-finish`,
    {
      body: {
        organization_id: scope.organization_id,
        ...input,
      },
    },
  );
}

export function autoReconcile(
  bank_account_id: string,
  input: { confidence_threshold: number },
): Promise<{ checked: number; reconciled: number; skipped: number }> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/bank-accounts/${bank_account_id}/auto-reconcile`,
    {
      body: {
        organization_id: scope.organization_id,
        ...input,
      },
    },
  );
}

export interface StatementImport {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  bank_account_id: string;
  format: string;
  file_name: string;
  account_identifier?: string;
  statement_currency?: string;
  statement_start_date?: string;
  statement_end_date?: string;
  opening_balance_cents?: number;
  closing_balance_cents?: number;
  warnings: string[];
  metadata: unknown;
  imported_count: number;
  duplicate_count: number;
  status: string;
}

export function listStatementImports(
  bank_account_id: string,
): Promise<StatementImport[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/bank-statement-imports", {
    query: {
      organization_id: scope.organization_id,
      bank_account_id,
    },
  });
}

export type StatementContentEncoding = "text" | "base64";

interface StatementMapping {
  date?: string;
  description?: string;
  amount?: string;
  debit?: string;
  credit?: string;
  balance?: string;
  reference?: string;
  payee?: string;
  counterparty?: string;
}

interface ImportStatementInput {
  bank_account_id: string;
  file_name?: string;
  content: string;
  format?: string;
  content_encoding?: StatementContentEncoding;
  sheet_name?: string;
  mapping?: StatementMapping;
}

export function importStatement(input: ImportStatementInput): Promise<{
  id: string;
  organization_id: string;
  imported: number;
  duplicate_count: number;
}> {
  return request("POST", "/finance/invoicing/bank-statement-imports", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export interface ParsedTransaction {
  date: string;
  posted_date?: string;
  description: string;
  amount_cents: number;
  balance_cents?: number;
  reference?: string;
  payee?: string;
  counterparty?: string;
  currency_code?: string;
  external_transaction_id?: string;
  statement_line_ref?: string;
  pending: boolean;
  raw?: unknown;
}

export interface StatementPreview {
  format: string;
  transaction_count: number;
  transactions: ParsedTransaction[];
  warnings: string[];
}

export function previewStatementImport(
  input: Omit<ImportStatementInput, "bank_account_id">,
): Promise<StatementPreview> {
  return request("POST", "/finance/invoicing/bank-statement-imports/preview", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// --- Scheduled payments (Phase 6 Banking) --------------------------------

export interface ScheduledPaymentRecord {
  id: string;
  organization_id: string;
  bill_id: string;
  supplier_name: string;
  amount_cents: number;
  scheduled_date: string;
  status: "pending" | "processing" | "completed" | "failed" | "cancelled";
  processed_at: string | null;
  notes: string | null;
}

interface CreateScheduledPaymentInput {
  bill_id: string;
  supplier_name: string;
  amount_cents: number;
  /** Calendar date (YYYY-MM-DD). */
  scheduled_date: string;
  notes?: string;
}

export function createScheduledPayment(
  input: CreateScheduledPaymentInput,
): Promise<{ id: string; organization_id: string }> {
  return request("POST", "/finance/invoicing/scheduled-payments", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function listScheduledPayments(): Promise<ScheduledPaymentRecord[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/scheduled-payments", {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

export function processScheduledPayments(input: {
  as_of: string;
}): Promise<{ processed: number }> {
  return request("POST", "/finance/invoicing/scheduled-payments/process", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// --- Payment batches (Phase 6 Banking) -----------------------------------

export interface PaymentBatchItemRecord {
  id: string;
  bill_id: string;
  supplier_name: string;
  amount_cents: number;
  status: "pending" | "completed" | "failed";
}

export interface PaymentBatchRecord {
  id: string;
  organization_id: string;
  name: string;
  status: "draft" | "submitted" | "completed";
  total_amount_cents: number;
  items: PaymentBatchItemRecord[];
}

export interface PaymentBatchItemInput {
  bill_id: string;
  supplier_name: string;
  amount_cents: number;
}

export function createPaymentBatch(input: {
  name: string;
  items: PaymentBatchItemInput[];
}): Promise<{ id: string; organization_id: string }> {
  return request("POST", "/finance/invoicing/payment-batches", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function listPaymentBatches(): Promise<PaymentBatchRecord[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/payment-batches", {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

export function submitPaymentBatch(
  batch_id: string,
  input: { as_of: string },
): Promise<{ id: string; processed: number }> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/payment-batches/${batch_id}/submit`,
    {
      body: {
        organization_id: scope.organization_id,
        ...input,
      },
    },
  );
}

interface RemittanceLine {
  bill_id: string;
  bill_number: string;
  bill_date: string;
  bill_total_cents: number;
  amount_paid_cents: number;
}

export interface RemittanceGroup {
  supplier_name: string;
  total_paid_cents: number;
  lines: RemittanceLine[];
}

export interface BatchRemittance {
  batch_id: string;
  batch_name: string;
  groups: RemittanceGroup[];
  grand_total_paid_cents: number;
}

export function getBatchRemittance(batch_id: string): Promise<BatchRemittance> {
  const scope = currentOrganizationScope();
  return request(
    "GET",
    `/finance/invoicing/payment-batches/${batch_id}/remittance`,
    {
      query: {
        organization_id: scope.organization_id,
      },
    },
  );
}

// --- Phase 6 Tax ----------------------------------------------------------
// Rates, components, brackets, allowance configs, jurisdiction lookup,
// profile seeding, the compute-only return, and the yearly contractor
// (1099) report. Shapes match specs/typespec/branches/finance/tax.tsp,
// not the generated client.
//
// Deliberately absent (no backend route/handler/spec op): filed-return
// records and filing status/submit endpoints, dedicated VAT/BAS/Schedule-C
// computations, sales-tax reports, tax periods, and currency settings.

type TaxRateType = "sales" | "purchase" | "both";

export const TAX_RATE_TYPES: TaxRateType[] = ["sales", "purchase", "both"];

export const TAX_RATE_KINDS = [
  "standard",
  "blocked",
  "partial_block",
  "exempt",
  "reverse_charge",
  "no_vat",
  "sales_tax_us",
] as const;

export interface TaxRate {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  name: string;
  rate_bp: number;
  rate_type: string;
  kind: string;
  recoverable_percent_bp: number;
  is_default: boolean;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

/** Basis points (100 = 1%) rendered as a percent string. */
export function rateBpLabel(rate_bp: number): string {
  return `${(rate_bp / 100).toFixed(2)}%`;
}

export function listTaxRates(): Promise<TaxRate[]> {
  return request("GET", "/finance/tax/rates", { query: currentOrganizationScope() });
}

interface CreateTaxRateInput {
  name: string;
  rate_bp: number;
  rate_type: TaxRateType | string;
  kind: (typeof TAX_RATE_KINDS)[number] | string;
  recoverable_percent_bp: number;
  is_default: boolean;
}

export function createTaxRate(
  input: CreateTaxRateInput,
): Promise<{ id: string; organization_id: string; legal_entity_id: string }> {
  return request("POST", "/finance/tax/rates", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function updateTaxRate(
  rate_id: string,
  input: Partial<
    Pick<
      CreateTaxRateInput,
      | "name"
      | "rate_bp"
      | "rate_type"
      | "kind"
      | "recoverable_percent_bp"
      | "is_default"
    > & { is_active?: boolean }
  >,
): Promise<TaxRate> {
  return request("PUT", `/finance/tax/rates/${rate_id}`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function deleteTaxRate(
  rate_id: string,
): Promise<{ id: string; organization_id: string }> {
  const scope = currentOrganizationScope();
  return request("DELETE", `/finance/tax/rates/${rate_id}`, {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

export interface TaxComponent {
  id: string;
  tax_rate_id: string;
  name: string;
  rate_bp: number;
  account_id?: string;
}

export function listTaxComponents(rate_id: string): Promise<TaxComponent[]> {
  return request("GET", `/finance/tax/rates/${rate_id}/components`, {
    query: currentOrganizationScope(),
  });
}

export function createTaxComponent(
  rate_id: string,
  input: { name: string; rate_bp: number; account_id?: string },
): Promise<{ id: string; tax_rate_id: string; organization_id?: string }> {
  return request("POST", `/finance/tax/rates/${rate_id}/components`, {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function deleteTaxComponent(
  rate_id: string,
  component_id: string,
): Promise<{ id: string; organization_id: string }> {
  const scope = currentOrganizationScope();
  return request(
    "DELETE",
    `/finance/tax/rates/${rate_id}/components/${component_id}`,
    {
      query: {
        organization_id: scope.organization_id,
      },
    },
  );
}

interface TaxProfileRate {
  id?: string;
  name: string;
  rate_bp: number;
  reason?: string;
}

interface ApplyTaxProfileResult {
  country: string;
  regime: string;
  tax_name: string;
  organization_id: string;
  legal_entity_id: string;
  created: TaxProfileRate[];
  skipped: TaxProfileRate[];
}

export function applyTaxProfile(input: {
  country: string;
}): Promise<ApplyTaxProfileResult> {
  return request("POST", "/finance/tax/profiles/apply", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

interface EnsureTaxSeededResult {
  organization_id: string;
  legal_entity_id: string;
  seeded?: boolean;
  country?: string;
  regime?: string;
  tax_name?: string;
  created?: TaxProfileRate[];
  skipped?: TaxProfileRate[];
}

export function ensureTaxSeeded(input: {
  country?: string;
  tax_regime?: string;
}): Promise<EnsureTaxSeededResult> {
  return request("POST", "/finance/tax/profiles/ensure-seeded", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export interface TaxLookupResult {
  combined_rate_bp: number;
  state_rate_bp: number;
  county_rate_bp: number;
  city_rate_bp: number;
  special_rate_bp: number;
  source: string;
  country: string;
  state?: string;
  postal_code?: string;
}

/** Null means no saved rate covers that address yet — not an error. */
export function lookupTaxJurisdiction(input: {
  country: string;
  state?: string;
  postal_code?: string;
}): Promise<TaxLookupResult | null> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/tax/jurisdictions/lookup", {
    query: {
      organization_id: scope.organization_id,
      ...input,
    },
  });
}

interface SaveJurisdictionInput {
  country: string;
  state?: string;
  county?: string;
  city?: string;
  postal_code?: string;
  combined_rate_bp: number;
  state_rate_bp: number;
  county_rate_bp: number;
  city_rate_bp: number;
  special_rate_bp: number;
  source: "manual" | "cached";
}

// Save-only: no jurisdiction list endpoint exists, so the page confirms
// from the returned record rather than a saved list.
export function saveTaxJurisdiction(
  input: SaveJurisdictionInput,
): Promise<TaxLookupResult & { id: string; organization_id: string }> {
  return request("POST", "/finance/tax/jurisdictions", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

type JurisdictionLevel = "federal" | "state" | "local";

export interface TaxBracket {
  id: string;
  organization_id: string;
  name: string;
  jurisdiction_level: string;
  jurisdiction?: string;
  filing_status?: string;
  tax_year?: number;
  min_income_cents: number;
  max_income_cents?: number;
  rate_bp: number;
  base_amount_cents?: number;
  standard_deduction_cents?: number;
  is_active: boolean;
  created_at?: string;
}

export function listTaxBrackets(): Promise<TaxBracket[]> {
  return request("GET", "/finance/tax/brackets", {
    query: currentOrganizationScope(),
  });
}

interface CreateTaxBracketInput {
  name: string;
  jurisdiction_level: JurisdictionLevel | string;
  jurisdiction?: string;
  filing_status?: string;
  tax_year?: number;
  min_income_cents: number;
  max_income_cents?: number;
  rate_bp: number;
  base_amount_cents?: number;
  standard_deduction_cents?: number;
}

export function createTaxBracket(
  input: CreateTaxBracketInput,
): Promise<{ id: string; organization_id: string }> {
  return request("POST", "/finance/tax/brackets", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function deleteTaxBracket(
  bracket_id: string,
): Promise<{ id: string; organization_id: string }> {
  return request("DELETE", `/finance/tax/brackets/${bracket_id}`, {
    query: currentOrganizationScope(),
  });
}

export interface AllowanceConfig {
  id: string;
  organization_id: string;
  jurisdiction_level: string;
  jurisdiction?: string;
  tax_year: number;
  allowance_value_cents: number;
  standard_deduction_cents: number;
  created_at?: string;
}

export function listAllowanceConfigs(): Promise<AllowanceConfig[]> {
  return request("GET", "/finance/tax/allowance-configs", {
    query: currentOrganizationScope(),
  });
}

export function createAllowanceConfig(input: {
  jurisdiction_level: JurisdictionLevel | string;
  jurisdiction?: string;
  tax_year: number;
  allowance_value_cents: number;
  standard_deduction_cents: number;
}): Promise<{ id: string; organization_id: string }> {
  return request("POST", "/finance/tax/allowance-configs", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function deleteAllowanceConfig(
  config_id: string,
): Promise<{ id: string; organization_id: string }> {
  return request("DELETE", `/finance/tax/allowance-configs/${config_id}`, {
    query: currentOrganizationScope(),
  });
}

export interface TaxReturnBox {
  box: string;
  label: string;
  amount_cents: number;
}

export interface TaxReturn {
  organization_id: string;
  legal_entity_id: string;
  start_date: string;
  end_date: string;
  basis: string;
  output_vat_cents: number;
  input_vat_cents: number;
  net_vat_cents: number;
  boxes: TaxReturnBox[];
}

// Compute-only: the backend calculates the boxes for a date range but
// stores no filed return — there is no filing-status or submit endpoint.
export function getTaxReturn(input: {
  start_date: string;
  end_date: string;
  basis?: "accrual" | "cash";
}): Promise<TaxReturn> {
  return request("GET", "/finance/tax/returns", {
    query: { ...currentOrganizationScope(), ...input },
  });
}

export interface Tax1099Vendor {
  contact_id: string;
  tax_identifier?: string;
  total_paid_cents: number;
  payment_count: number;
  reportable: boolean;
}

export interface Tax1099Report {
  organization_id: string;
  legal_entity_id: string;
  tax_year: number;
  start_date: string;
  end_date: string;
  threshold_cents: number;
  vendors: Tax1099Vendor[];
  reportable_count: number;
  reportable_total_cents: number;
  grand_total_cents: number;
}

// Read-only yearly rollup of supplier payments for contractor reporting
// (the 1099 report). No filing endpoint exists — this is the numbers table
// behind the filing, not the filing itself.
export function getTax1099Report(input: {
  tax_year: number;
  threshold_cents?: number;
}): Promise<Tax1099Report> {
  return request("GET", "/finance/tax/reports/1099", {
    query: {
      ...currentOrganizationScope(),
      tax_year: String(input.tax_year),
      ...(input.threshold_cents !== undefined
        ? { threshold_cents: String(input.threshold_cents) }
        : {}),
    },
  });
}

// Manual exchange rates for the current legal entity. There is no rate feed.
// Response shapes are the generated TypeSpec contracts.
export type ExchangeRateRecord = FinanceExchangeRateResponse;
export function listExchangeRates(): Promise<FinanceExchangeRateListResponse> {
  return request("GET", "/finance/invoicing/exchange-rates", {
    query: currentOrganizationScope(),
  });
}

export function recordExchangeRate(
  input: Omit<
    FinanceExchangeRateRecordRequest,
    "organization_id" | "legal_entity_id"
  >,
): Promise<ExchangeRateRecord> {
  return request("POST", "/finance/invoicing/exchange-rates", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function resolveExchangeRate(input: {
  base_currency: string;
  target_currency: string;
  as_of: string;
}): Promise<FinanceExchangeRateResolveResponse> {
  return request("GET", "/finance/invoicing/exchange-rates/resolve", {
    query: { ...currentOrganizationScope(), ...input },
  });
}

// Source-backed tax estimates. The caller supplies the verified rule; the
// server refuses to guess an unsupported jurisdiction.
export type TaxEstimateRule = FinanceTaxEstimateRule;
export type TaxQuote = FinanceTaxEstimateQuoteResponse;

type TaxQuoteInput = Omit<
  FinanceTaxEstimateQuoteRequest,
  "organization_id"
>;

export function quoteTaxEstimate(input: TaxQuoteInput): Promise<TaxQuote> {
  return request("POST", "/finance/tax/estimates/quote", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function priceTaxTargetNet(input: TaxQuoteInput): Promise<TaxQuote> {
  return request("POST", "/finance/tax/estimates/target-net", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export type TaxReserveEstimate = FinanceTaxEstimateReserveResponse;

export function estimateTaxReserve(
  input:
    | Omit<FinanceTaxEstimateReserveBooksRequest, "organization_id">
    | Omit<FinanceTaxEstimateReserveManualRequest, "organization_id">,
): Promise<TaxReserveEstimate> {
  return request("POST", "/finance/tax/estimates/reserve", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// --- Phase 6 extended reports --------------------------------------------
// Cash-flow forecast, location breakdowns, job profitability, and group
// consolidation. Shapes match the `FinanceCashFlowForecast*` /
// `FinanceAnalyticReport*` / `FinanceJobProfitability*` /
// `FinanceConsolidation*` models in
// specs/typespec/branches/finance/invoicing.tsp.

interface ForecastWeek {
  week_of: string;
  inflows_cents: number;
  outflows_cents: number;
  net_cents: number;
  cumulative_net_cents: number;
  entry_count: number;
}

interface ForecastEntry {
  date: string;
  entry_type: string;
  description: string;
  amount_cents: number;
}

interface CashFlowForecast {
  start_date: string;
  end_date: string;
  weeks_ahead: number;
  total_inflows_cents: number;
  total_outflows_cents: number;
  net_forecast_cents: number;
  weeks: ForecastWeek[];
  entries: ForecastEntry[];
}

export function getCashFlowForecast(
  input: { weeks?: number } = {},
): Promise<CashFlowForecast> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/reports/cash-flow-forecast", {
    query: {
      organization_id: scope.organization_id,
      ...(input.weeks !== undefined ? { weeks: String(input.weeks) } : {}),
    },
  });
}

export type AnalyticDimension = "location";

interface AnalyticAccount {
  account_id: string;
  code: string;
  name: string;
  account_type: string;
  sub_type?: string;
  debit_cents: number;
  credit_cents: number;
  balance_cents: number;
}

export interface AnalyticGroup {
  dimension_value?: string;
  accounts: AnalyticAccount[];
}

export interface AnalyticReport {
  groups: AnalyticGroup[];
}

export function getAnalyticReport(input: {
  dimension: AnalyticDimension;
  start_date: string;
  end_date: string;
  basis?: ReportBasis;
}): Promise<AnalyticReport> {
  return request("GET", "/finance/invoicing/reports/analytic", {
    query: { ...currentOrganizationScope(), ...input },
  });
}

export interface JobProfitabilityEntry {
  job_id?: string;
  budget_cents: number;
  estimated_minutes: number;
  fixed_price_cents: number;
  hourly_rate_cents: number;
  revenue_cents: number;
  costs_cents: number;
  material_cost_cents: number;
  other_cost_cents: number;
  labor_cost_cents: number;
  profit_cents: number;
  margin_basis_points: number;
  total_minutes: number;
  billable_minutes: number;
  budget_variance_cents: number;
  budget_used_basis_points: number;
  hours_variance_minutes: number;
  hours_used_basis_points: number;
}

export interface JobProfitabilityReport {
  start_date: string;
  end_date: string;
  entries: JobProfitabilityEntry[];
  total_revenue_cents: number;
  total_costs_cents: number;
  total_profit_cents: number;
}

export function getJobProfitability(input: {
  start_date: string;
  end_date: string;
}): Promise<JobProfitabilityReport> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/reports/job-profitability", {
    query: {
      organization_id: scope.organization_id,
      ...input,
    },
  });
}

// The GL-query workbook below was the first export; the trial-balance,
// balance-sheet, profit-and-loss, aged-receivables, aged-payables, and
// cash-flow XLSX twins now live alongside it (see the report-workbook
// section further down), each rendering the same domain object as its JSON
// sibling through `render_statement_workbook` in
// backend/service/src/controllers/hardening.rs.

interface GlQueryXlsxParams {
  start_date: string;
  end_date: string;
  basis?: ReportBasis;
}

/** Shared Bearer-blob download: GET a file path with scope query, save the
 * response blob under `filename`. Every binary export below (PDF + XLSX +
 * backup JSON) goes through this so auth headers stay in one place. */
async function downloadBlob(
  path: string,
  query: Record<string, string | string[] | undefined>,
  filename: string,
): Promise<void> {
  const url = buildUrl(path, query);
  if (isUiFixtureMode) return;
  const headers: Record<string, string> = {};
  const csrf = getStoredCsrfToken();
  if (csrf) headers["x-csrf-token"] = csrf;
  // Dev fallback only — same rule as `request()` above.
  const fallback = devBearerToken();
  if (fallback) headers.Authorization = `Bearer ${fallback}`;
  const response = await fetch(url, { credentials: "include", headers });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new ApiRequestError(
      response.status,
      path,
      `GET ${path} failed: ${response.status} ${detail}`.trim(),
    );
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function downloadGlQueryXlsx(
  params: GlQueryXlsxParams,
): Promise<void> {
  return downloadBlob(
    "/finance/invoicing/reports/gl-query.xlsx",
    { ...currentOrganizationScope(), ...params },
    "gl-query.xlsx",
  );
}

// --- Report workbook twins (.xlsx) ------------------------------------------
// Each renders the same domain object as its JSON sibling (see
// `export_*_xlsx` in
// backend/service/src/controllers/hardening.rs). Query shapes
// mirror the JSON fetchers above so pages pass their applied params straight
// through.

export function downloadTrialBalanceXlsx(
  params: AsAtReportParams = {},
): Promise<void> {
  const asAt = params.as_at ?? new Date().toISOString().slice(0, 10);
  return downloadBlob(
    "/finance/invoicing/reports/trial-balance.xlsx",
    { ...currentOrganizationScope(), ...params },
    `trial-balance-${asAt}.xlsx`,
  );
}

export function downloadBalanceSheetXlsx(
  params: AsAtReportParams = {},
): Promise<void> {
  const asAt = params.as_at ?? new Date().toISOString().slice(0, 10);
  return downloadBlob(
    "/finance/invoicing/reports/balance-sheet.xlsx",
    { ...currentOrganizationScope(), ...params },
    `balance-sheet-${asAt}.xlsx`,
  );
}

export function downloadProfitAndLossXlsx(
  params: ProfitAndLossParams,
): Promise<void> {
  return downloadBlob(
    "/finance/invoicing/reports/profit-and-loss.xlsx",
    { ...currentOrganizationScope(), ...params },
    `profit-and-loss-${params.start_date}-${params.end_date}.xlsx`,
  );
}

export interface CultivationCloseSchedule {
  title: string;
  columns: string[];
  rows: Array<{ label: string; values: Array<string | number>; total?: boolean }>;
}

export interface CultivationCloseReport {
  period?: { label?: string; months?: string[] };
  schedules?: Record<string, CultivationCloseSchedule>;
}

export function getCultivationClose(params: {
  license_id: string;
  period_end: string;
}): Promise<CultivationCloseReport> {
  return request("GET", "/finance/invoicing/reports/cultivation-close", {
    query: { ...currentOrganizationScope(), ...params },
  });
}

const PRIOR_PLANT_PHASES = ["mother", "immature", "vegetative", "flowering"] as const;

/** The calendar month immediately before a close that starts on the first. */
export function previousCloseMonthEnd(periodStart: string): { month: string; period_end: string } | null {
  const match = /^(\d{4})-(\d{2})-01$/.exec(periodStart);
  if (!match) return null;
  let year = Number(match[1]);
  let month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  if (month === 1) {
    year -= 1;
    month = 12;
  } else {
    month -= 1;
  }
  const label = `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { month: label, period_end: `${label}-${String(last).padStart(2, "0")}` };
}

/** Reads the ending plant counts stamped on a locked close. Missing counts stay missing. */
export function priorPlantCountsFromClose(packet: unknown, month: string): {
  month: string;
  input_identity: string;
  ending_counts: Record<(typeof PRIOR_PLANT_PHASES)[number], number>;
} | null {
  if (!packet || typeof packet !== "object" || Array.isArray(packet)) return null;
  const ending = (packet as { ending_plant_counts?: unknown }).ending_plant_counts;
  if (!ending || typeof ending !== "object" || Array.isArray(ending)) return null;
  const row = ending as { month?: unknown; input_identity?: unknown; counts?: unknown };
  if (row.month !== month || typeof row.input_identity !== "string" || !/^[0-9a-f]{64}$/.test(row.input_identity)) return null;
  if (!row.counts || typeof row.counts !== "object" || Array.isArray(row.counts)) return null;
  const counts = row.counts as Record<string, unknown>;
  const ending_counts = {} as Record<(typeof PRIOR_PLANT_PHASES)[number], number>;
  for (const phase of PRIOR_PLANT_PHASES) {
    const value = counts[phase];
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) return null;
    ending_counts[phase] = value;
  }
  return { month, input_identity: row.input_identity, ending_counts };
}

const CARRIED_PHASES = ["immature", "vegetative", "flowering", "harvest"] as const;
const CARRIED_ACCOUNTS = new Set([
  "raw_materials", "direct_labor", "finished_goods", "destroy_expense", "waste_expense", "cogs",
  "mother", "immature", "vegetative", "flowering", "harvest",
]);

export type PriorCloseSettings = {
  month: string;
  values: Record<string, string>;
  allocations: Array<{ pool: string; account: string; name: string; amount: string; percent: string; labor: string }>;
};

function carriedRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function carriedMoney(cents: unknown): string | null {
  if (typeof cents !== "number" || !Number.isSafeInteger(cents) || cents < 0) return null;
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, "0")}`;
}

function carriedDecimal(value: unknown): string | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return null;
  const text = value.toFixed(12).replace(/\.?0+$/, "");
  return /^\d+(?:\.\d+)?$/.test(text) ? text : null;
}

function carriedCount(value: unknown): string | null {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value <= 0 || value > 10_000) return null;
  return String(value);
}

/** Stable location inputs from the prior locked close. Period amounts and the frozen snapshot stay out. */
export function priorSettingsFromClose(packet: unknown, month: string): PriorCloseSettings | null {
  const root = carriedRecord(packet);
  const reviewed = carriedRecord(root?.reviewed_inputs);
  if (!reviewed || !/^\d{4}-\d{2}$/.test(month)) return null;
  const review = carriedRecord(reviewed.review_inputs);
  const rates = carriedRecord(review?.rate_inputs);
  const applied = carriedRecord(rates?.applied_rates) ?? carriedRecord(review?.applied_rates);
  const meta = carriedRecord(reviewed.journal_meta);
  const journals = carriedRecord(reviewed.journal_inputs);
  const values: Record<string, string> = {};
  for (const phase of CARRIED_PHASES) {
    const weeks = carriedCount(rates?.[`phase_weeks_${phase}`]);
    if (weeks) values[`weeks_${phase}`] = weeks;
    const rate = carriedDecimal(applied?.[phase]);
    if (rate) values[`rate_${phase}`] = rate;
  }
  const reserve = carriedMoney(rates?.reserve_cents);
  if (reserve) values.reserve = reserve;
  if (typeof review?.excise_rate_basis_points === "number" && Number.isSafeInteger(review.excise_rate_basis_points) && review.excise_rate_basis_points >= 0) {
    values.excise_rate_basis_points = String(review.excise_rate_basis_points);
  }
  if (typeof meta?.rounding_tolerance_cents === "number" && Number.isSafeInteger(meta.rounding_tolerance_cents) && meta.rounding_tolerance_cents >= 0) {
    values.rounding_tolerance = String(meta.rounding_tolerance_cents);
  }
  if (typeof meta?.client_name === "string" && meta.client_name.trim() && meta.client_name.trim().length <= 80) {
    values.client_name = meta.client_name.trim();
  }
  if (reviewed.mode === "draft" || reviewed.mode === "post") values.posting_mode = reviewed.mode;
  const wip = carriedRecord(journals?.wip);
  const wipInputs = carriedRecord(wip?.inputs);
  if (typeof wipInputs?.inventory_asset_wip_credit_key === "string" && ["mother", ...CARRIED_PHASES].includes(wipInputs.inventory_asset_wip_credit_key)) {
    values.inventory_credit_phase = wipInputs.inventory_asset_wip_credit_key;
  }
  for (const family of Object.values(journals ?? {})) {
    const accounts = carriedRecord(carriedRecord(family)?.accounts);
    if (!accounts) continue;
    for (const [key, account] of Object.entries(accounts)) {
      if (!CARRIED_ACCOUNTS.has(key)) continue;
      const ref = carriedRecord(account)?.account_ref;
      if (typeof ref === "string" && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,39}$/.test(ref)) values[`account_${key}`] = ref;
    }
  }
  const direct = carriedRecord(journals?.directexall);
  const allocations = Array.isArray(direct?.allocations) ? direct.allocations.flatMap((row) => {
    const item = carriedRecord(row);
    if (!item) return [];
    const pool = typeof item.cost_pool_id === "string" ? item.cost_pool_id.trim() : "";
    const account = typeof item.account_ref === "string" ? item.account_ref.trim() : "";
    const name = typeof item.account_name === "string" ? item.account_name.trim() : "";
    if (!pool || !account || !name || [pool, account, name].some((value) => value.length > 200)) return [];
    const amount = carriedMoney(item.allocated_amount_cents);
    const basis = typeof item.basis_points === "number" && Number.isSafeInteger(item.basis_points) && item.basis_points >= 0 && item.basis_points <= 10_000
      ? carriedDecimal(item.basis_points / 100)
      : null;
    return [{
      pool,
      account,
      name,
      amount: amount && amount !== "0.00" ? amount : "",
      percent: basis ?? "",
      labor: item.direct_labor === true ? "yes" : "",
    }];
  }).slice(0, 100) : [];
  if (Object.keys(values).length === 0 && allocations.length === 0) return null;
  return { month, values, allocations };
}

export type CarriedPlantMonth = {
  year: number;
  month: number;
  begin_end: number[];
  adjustments: { mother: number; immature: number; vegetative: number; flowering: number };
};

/** One locked month's typed plant grid. A blank cell means the month is not carried. */
export function plantMonthFromClose(packet: unknown, month: string): CarriedPlantMonth | null {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return null;
  const year = Number(match[1]);
  const monthNumber = Number(match[2]);
  const reviewed = carriedRecord(carriedRecord(packet)?.reviewed_inputs);
  const inventory = carriedRecord(carriedRecord(reviewed?.review_inputs)?.manual_inventory);
  const months = inventory?.months;
  if (!Array.isArray(months)) return null;
  const found = months.find((item) => carriedRecord(item)?.year === year && carriedRecord(item)?.month === monthNumber);
  const row = carriedRecord(found);
  if (!row) return null;
  const grid = row.begin_end;
  if (!Array.isArray(grid) || grid.length !== 12 || grid.some((cell) => typeof cell !== "number" || !Number.isSafeInteger(cell) || cell < 0)) return null;
  const adjustments = { mother: 0, immature: 0, vegetative: 0, flowering: 0 };
  const raw = carriedRecord(row.adjustments);
  if (raw) {
    for (const phase of ["mother", "immature", "vegetative", "flowering"] as const) {
      const value = raw[phase] ?? 0;
      if (typeof value !== "number" || !Number.isSafeInteger(value)) return null;
      adjustments[phase] = value;
    }
  }
  return { year, month: monthNumber, begin_end: grid as number[], adjustments };
}

/** Locked plant grids for the earlier months of the same year, stopping at the first gap. */
export async function collectEarlierPlantMonths(
  licenseId: string,
  periodStart: string,
  read: (periodEnd: string) => Promise<unknown>,
): Promise<CarriedPlantMonth[]> {
  const found: CarriedPlantMonth[] = [];
  let cursor = periodStart;
  for (let step = 0; step < 11; step += 1) {
    const previous = previousCloseMonthEnd(cursor);
    if (!previous || previous.month.slice(0, 4) !== periodStart.slice(0, 4)) break;
    let packet: unknown = null;
    try {
      packet = await read(previous.period_end);
    } catch {
      packet = null;
    }
    const grid = plantMonthFromClose(packet, previous.month);
    if (!grid) break;
    found.unshift(grid);
    cursor = `${previous.month}-01`;
  }
  return found;
}

export function downloadCultivationCloseXlsx(
  params: { license_id: string; period_end: string },
): Promise<void> {
  return downloadBlob(
    "/finance/invoicing/reports/cultivation-close.xlsx",
    { ...currentOrganizationScope(), ...params },
    `cultivation-close-${params.period_end}.xlsx`,
  );
}

export interface CultivationCapabilities {
  view: boolean;
  import: boolean;
  review: boolean;
  approve: boolean;
  post: boolean;
  lock: boolean;
  correct: boolean;
  configure: boolean;
}

export type CultivationCapability = keyof CultivationCapabilities;

export interface CultivationCapabilitiesResponse {
  organization_id: string;
  legal_entity_id?: string | null;
  capabilities: CultivationCapabilities;
}

export function getCultivationCapabilities(input: {
  legal_entity_id?: string;
} = {}): Promise<CultivationCapabilitiesResponse> {
  return request("GET", "/finance/invoicing/cultivation/capabilities", {
    query: {
      organization_id: currentOrganizationScope().organization_id,
      legal_entity_id: input.legal_entity_id,
    },
  });
}

export function downloadCultivationClosePack(params: {
  legal_entity_id: string;
  license_id: string;
  period_start: string;
  period_end: string;
}): Promise<void> {
  return downloadBlob(
    "/finance/invoicing/reports/cultivation-close/pack",
    { ...currentOrganizationScope(), ...params },
    `cultivation-close-pack-${params.period_start}_${params.period_end}.zip`,
  );
}

export type CultivationPackLastStatus = "ready" | "failed" | null;

export interface CultivationClosePackSchedule {
  schedule_id: string;
  legal_entity_id: string;
  license_id: string;
  day_of_month: number;
  enabled: boolean;
  next_run_on?: string | null;
  last_run_on?: string | null;
  last_status?: CultivationPackLastStatus;
  last_error?: string | null;
  last_pack_sha256?: string | null;
  download_available: boolean;
  created_by_user_id: number;
  created_at: string;
  updated_at: string;
}

export function listCultivationClosePackSchedules(input: {
  legal_entity_id: string;
  license_id: string;
}): Promise<{ schedules: CultivationClosePackSchedule[] }> {
  return request("GET", "/finance/invoicing/reports/cultivation-close/pack-schedules", {
    query: { ...currentOrganizationScope(), ...input },
  });
}

export function createCultivationClosePackSchedule(input: {
  legal_entity_id: string;
  license_id: string;
  day_of_month: number;
  enabled?: boolean;
}): Promise<{ schedule: CultivationClosePackSchedule }> {
  return request("POST", "/finance/invoicing/reports/cultivation-close/pack-schedules", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function updateCultivationClosePackSchedule(
  scheduleId: string,
  input: { day_of_month?: number; enabled?: boolean },
): Promise<{ schedule: CultivationClosePackSchedule }> {
  return request(
    "PUT",
    `/finance/invoicing/reports/cultivation-close/pack-schedules/${scheduleId}`,
    { body: { ...currentOrganizationScope(), ...input } },
  );
}

export function enableCultivationClosePackSchedule(
  scheduleId: string,
): Promise<{ schedule: CultivationClosePackSchedule }> {
  return request(
    "POST",
    `/finance/invoicing/reports/cultivation-close/pack-schedules/${scheduleId}/enable`,
    { body: currentOrganizationScope() },
  );
}

export function disableCultivationClosePackSchedule(
  scheduleId: string,
): Promise<{ schedule: CultivationClosePackSchedule }> {
  return request(
    "POST",
    `/finance/invoicing/reports/cultivation-close/pack-schedules/${scheduleId}/disable`,
    { body: currentOrganizationScope() },
  );
}

export function downloadCultivationClosePackSchedule(
  scheduleId: string,
): Promise<void> {
  return downloadBlob(
    `/finance/invoicing/reports/cultivation-close/pack-schedules/${scheduleId}/download`,
    { organization_id: currentOrganizationScope().organization_id },
    `cultivation-close-pack-${scheduleId}.zip`,
  );
}

export function downloadAgedReceivablesXlsx(
  params: { as_of?: string } = {},
): Promise<void> {
  const asOf = params.as_of ?? new Date().toISOString().slice(0, 10);
  return downloadBlob(
    "/finance/invoicing/reports/aged-receivables.xlsx",
    { ...currentOrganizationScope(), ...params },
    `aged-receivables-${asOf}.xlsx`,
  );
}

export function downloadAgedPayablesXlsx(
  params: { as_of?: string } = {},
): Promise<void> {
  const asOf = params.as_of ?? new Date().toISOString().slice(0, 10);
  return downloadBlob(
    "/finance/invoicing/reports/aged-payables.xlsx",
    { ...currentOrganizationScope(), ...params },
    `aged-payables-${asOf}.xlsx`,
  );
}

export function downloadCashFlowXlsx(
  params: {
    start_date?: string;
    end_date?: string;
    method?: "indirect" | "direct";
    basis?: ReportBasis;
  } = {},
): Promise<void> {
  const end = params.end_date ?? new Date().toISOString().slice(0, 10);
  return downloadBlob(
    "/finance/invoicing/reports/cash-flow.xlsx",
    { ...currentOrganizationScope(), ...params },
    `cash-flow-${end}.xlsx`,
  );
}

// --- Invoice + supplier-statement + remittance PDFs --------------------------
// `invoice_pdf` and `supplier_statement_pdf` in hardening.rs,
// `get_batch_remittance_pdf` in crate_surfaces.rs. All served as
// `attachment_response` PDFs through the same Bearer-blob pattern.

/** Downloads the rendered PDF for one invoice
 * (`GET /finance/invoicing/invoices/{id}.pdf`). */
export function downloadInvoicePdf(invoiceId: string): Promise<void> {
  return downloadBlob(
    `/finance/invoicing/invoices/${invoiceId}.pdf`,
    currentOrganizationScope(),
    `invoice-${invoiceId}.pdf`,
  );
}

interface SupplierStatementPdfParams {
  supplier_name: string;
  start_date: string;
  end_date: string;
  currency?: string;
}

/** Downloads the supplier statement PDF
 * (`GET /finance/invoicing/supplier-statements.pdf`). Requires an exact
 * supplier name plus a date range — the handler 400s otherwise. */
export function downloadSupplierStatementPdf(
  params: SupplierStatementPdfParams,
): Promise<void> {
  return downloadBlob(
    "/finance/invoicing/supplier-statements.pdf",
    { ...currentOrganizationScope(), ...params },
    "supplier-statement.pdf",
  );
}

/** Downloads the remittance advice PDF for one payment batch
 * (`GET /finance/invoicing/payment-batches/{batch_id}/remittance.pdf`). */
export function downloadRemittancePdf(batchId: string): Promise<void> {
  const scope = currentOrganizationScope();
  return downloadBlob(
    `/finance/invoicing/payment-batches/${batchId}/remittance.pdf`,
    {
      organization_id: scope.organization_id,
    },
    `remittance-${batchId}.pdf`,
  );
}

// --- Audit log (/audit-events) --------------------------------------------
// Filter + pagination params validated by `AuditEventsFilter` in api.rs
// (`action` is a prefix match, `limit` caps at 500, `page` is 1-based).
// Scoped by organization only — no legal-entity params.

export interface AuditEvent {
  id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  request_id: string;
  metadata: unknown;
  occurred_at: string;
  // Actor identity comes from the backend's join onto users (null when the
  // actor row no longer resolves). There is no separate user-directory
  // endpoint in this app — the audit list is the only actor source.
  actor_user_id: string | null;
  actor_email: string | null;
}

interface AuditEventsParams {
  action?: string;
  resource_type?: string;
  resource_id?: string;
  actor_user_id?: string;
  since?: string;
  until?: string;
  limit?: number;
  page?: number;
}

export function listAuditEvents(
  params: AuditEventsParams = {},
): Promise<AuditEvent[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/audit-events", {
    query: {
      organization_id: scope.organization_id,
      ...(params.action ? { action: params.action } : {}),
      ...(params.resource_type ? { resource_type: params.resource_type } : {}),
      ...(params.resource_id ? { resource_id: params.resource_id } : {}),
      ...(params.actor_user_id ? { actor_user_id: params.actor_user_id } : {}),
      ...(params.since ? { since: params.since } : {}),
      ...(params.until ? { until: params.until } : {}),
      ...(params.limit !== undefined ? { limit: String(params.limit) } : {}),
      ...(params.page !== undefined ? { page: String(params.page) } : {}),
    },
  });
}

// --- Dunning reminders (/finance/invoicing/reminders/*) ------------------
// Rule store + preview/queue composition in crate_surfaces.rs (nothing
// there sends); delivery in hardening.rs (`send_queued_reminders`, SMTP via
// `lettre` when configured, log provider otherwise).

export interface ReminderRule {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  document_type: string;
  trigger: string;
  trigger_days: number;
  recipient: string;
  custom_emails: string[];
  subject_template: string;
  body_template: string;
  enabled: boolean;
}

export interface ReminderCandidate {
  document_id: string;
  document_type: string;
  document_number: string;
  recipients: string[];
  recipient_source: string;
  crm_contact_id?: string;
  subject: string;
  body: string;
  amount_due_cents: number;
  days_overdue: number;
  dunning_level_after: number;
  rule_id?: string;
}

export interface ReminderSpec {
  document_type: "invoice" | "bill";
  trigger: "before_due" | "on_due" | "after_due";
  trigger_days?: number;
  recipient: "contact_email" | "contact_persons" | "custom";
  custom_emails?: string[];
  subject_template?: string;
  body_template?: string;
}

export function listReminderRules(): Promise<ReminderRule[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/reminders/rules", {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

export function createReminderRule(
  input: ReminderSpec,
): Promise<{ id: string; organization_id: string }> {
  return request("POST", "/finance/invoicing/reminders/rules", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

/** Evaluates an inline spec without storing a rule (rule-testing tool). */
export function previewReminders(
  input: ReminderSpec & { organization_name?: string; as_of?: string },
): Promise<ReminderCandidate[]> {
  return request("POST", "/finance/invoicing/reminders/preview", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

/** Stored rules evaluated minus the sent log — the actual send queue. */
export function getReminderQueue(
  params: { organization_name?: string; as_of?: string } = {},
): Promise<ReminderCandidate[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/reminders/queue", {
    query: {
      organization_id: scope.organization_id,
      ...params,
    },
  });
}

export interface ReminderSendPreview {
  dry_run: true;
  candidates: number;
  recipients: number;
}

/** Dry-run: reports the queue size without sending or logging anything. */
export function previewReminderSend(
  params: { organization_name?: string; as_of?: string } = {},
): Promise<ReminderSendPreview> {
  return request("POST", "/finance/invoicing/reminders/send", {
    body: { ...currentOrganizationScope(), dry_run: true, ...params },
  });
}

export interface ReminderSendOutcome {
  recipient_email: string;
  sent: boolean;
  error_message?: string;
}

export interface ReminderSendResult {
  provider: "smtp" | "log";
  attempted: number;
  sent: number;
  failed: number;
  outcomes: ReminderSendOutcome[];
}

/** Sends the queued reminders. One recipient's failure never blocks the
 * batch — check `failed`/`outcomes` after. */
export function sendReminders(
  params: { organization_name?: string; as_of?: string } = {},
): Promise<ReminderSendResult> {
  return request("POST", "/finance/invoicing/reminders/send", {
    body: { ...currentOrganizationScope(), ...params },
  });
}

// --- Document templates (/finance/invoicing/document-templates*) --------
// Per-document-type branding (invoice/quote/receipt/payslip/purchase-order:
// header/footer HTML, logo, accent color, tax/terms flags, bank details,
// payment instructions). Storage/CRUD only — no PDF rendering lives here.
// Shapes match `DocumentTemplateResponse` / `CreateDocumentTemplateRequest`
// / `UpdateDocumentTemplateRequest` in crate_surfaces.rs (`type` is the
// serde-renamed `template_type`; the enum serializes snake_case).

export type DocumentTemplateType =
  | "invoice"
  | "quote"
  | "receipt"
  | "payslip"
  | "purchase_order";

export const DOCUMENT_TEMPLATE_TYPES: DocumentTemplateType[] = [
  "invoice",
  "quote",
  "receipt",
  "payslip",
  "purchase_order",
];

export interface DocumentTemplate {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  name: string;
  type: DocumentTemplateType;
  header_html: string | null;
  footer_html: string | null;
  logo_url: string | null;
  accent_color: string;
  show_tax_breakdown: boolean;
  show_payment_terms: boolean;
  notes: string | null;
  bank_details: string | null;
  payment_instructions: string | null;
  is_default: boolean;
}

interface CreateDocumentTemplateInput {
  name: string;
  type: DocumentTemplateType;
  header_html?: string;
  footer_html?: string;
  logo_url?: string;
  accent_color?: string;
  show_tax_breakdown?: boolean;
  show_payment_terms?: boolean;
  notes?: string;
  bank_details?: string;
  payment_instructions?: string;
  is_default?: boolean;
}

export function listDocumentTemplates(): Promise<DocumentTemplate[]> {
  return request("GET", "/finance/invoicing/document-templates", {
    query: currentOrganizationScope(),
  });
}

export function createDocumentTemplate(
  input: CreateDocumentTemplateInput,
): Promise<{ id: string; organization_id: string; legal_entity_id: string }> {
  return request("POST", "/finance/invoicing/document-templates", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

interface UpdateDocumentTemplateInput {
  name?: string;
  header_html?: string;
  footer_html?: string;
  logo_url?: string;
  accent_color?: string;
  show_tax_breakdown?: boolean;
  show_payment_terms?: boolean;
  notes?: string;
  bank_details?: string;
  payment_instructions?: string;
  is_default?: boolean;
}

export function updateDocumentTemplate(
  templateId: string,
  input: UpdateDocumentTemplateInput,
): Promise<{ id: string; organization_id: string }> {
  const scope = currentOrganizationScope();
  return request("PUT", `/finance/invoicing/document-templates/${templateId}`, {
    body: {
      organization_id: scope.organization_id,
      ...input,
    },
  });
}

export function deleteDocumentTemplate(
  templateId: string,
): Promise<{ success: boolean }> {
  const scope = currentOrganizationScope();
  return request(
    "DELETE",
    `/finance/invoicing/document-templates/${templateId}`,
    {
      query: {
        organization_id: scope.organization_id,
      },
    },
  );
}

// --- Single-document reads (Phase 2 detail routes) --------------------------
// `GET /finance/invoicing/invoices/{invoice_id}`,
// `GET /finance/invoicing/bills/{bill_id}`, and
// `GET /finance/invoicing/journal-entries/{entry_id}`. Shapes match
// `FinanceInvoiceDetailResponse` / `FinanceBillDetailResponse` /
// `FinanceJournalEntryResponse` in crate_surfaces.rs (see also
// specs/typespec/branches/finance/invoicing.tsp). All three take the
// organization scope as query params; invoice/bill omit any status-history or
// transitions because no such tables exist — status mutates in place.

export interface InvoiceDetailLine {
  id: string;
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  tax_cents: number;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
}

export interface InvoiceDetail extends Invoice {
  sales_order_id?: string;
  lines: InvoiceDetailLine[];
  payments: InvoicePayment[];
  /** Worker-only review token, present only on an eligible draft single-get. */
  posting_review_sha256?: string;
}

export function getInvoiceDetail(invoice_id: string): Promise<InvoiceDetail> {
  return request("GET", `/finance/invoicing/invoices/${invoice_id}`, {
    query: currentOrganizationScope(),
  });
}

// Posts an eligible draft invoice. A changed invoice or locked period rejects
// with 409 (`invoice_posting_conflict` / `locked_period`); an audited resend of
// the same digest returns the same journal without creating a second one.
export function postInvoice(
  invoiceId: string,
  reviewedSha256: string,
): Promise<FinanceInvoicePostResponse> {
  const body: FinanceInvoicePostRequest = {
    ...currentOrganizationScope(),
    reviewed_sha256: reviewedSha256,
  };
  return request(
    "POST",
    `/finance/invoicing/invoices/${encodeURIComponent(invoiceId)}/post`,
    { body },
  );
}

export interface BillDetailLine {
  id: string;
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  tax_cents: number;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
}

export interface BillDetail extends Bill {
  lines: BillDetailLine[];
  payments: BillPayment[];
  /** Posted bill-source journal, when recognition succeeded. */
  recognition_journal_id?: string;
  /** Posted reversing journal, when the bill was voided after recognition. */
  reversal_journal_id?: string;
}

export function getBillDetail(bill_id: string): Promise<BillDetail> {
  return request("GET", `/finance/invoicing/bills/${bill_id}`, {
    query: currentOrganizationScope(),
  });
}

/** Single journal entry — same shape as the `JournalEntry` list rows. */
export function getJournalEntry(entry_id: string): Promise<JournalEntry> {
  return request("GET", `/finance/invoicing/journal-entries/${entry_id}`, {
    query: currentOrganizationScope(),
  });
}

// --- Credit notes / refunds (Phase 3 Money-in) ---------------------------
// Draft a note against one source invoice, mark it sent (or void), then
// apply it — the apply step posts the AR adjustment journals
// (DR revenue + DR output VAT / CR AR `1200`, source_type='credit_note')
// and flips the note to Applied (the idempotency guard: a second apply is
// rejected). Shapes match `credit_note_response` + the line projection in
// `get_credit_note` in crate_surfaces.rs.

export interface CreditNote {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  credit_note_number: string;
  customer_name: string;
  status: string;
  total_minor: number;
  issue_date: string;
  related_invoice_id?: string;
  crm_contact_id?: string;
}

export interface CreditNoteLine {
  id: string;
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  tax_cents: number;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
}

export interface CreditNoteDetail extends CreditNote {
  lines: CreditNoteLine[];
}

export interface CreateCreditNoteLineInput {
  account_id: string;
  description: string;
  amount_minor: number;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
}

interface CreateCreditNoteInput {
  invoice_id: string;
  customer_name: string;
  /** Calendar date (YYYY-MM-DD); the backend parses a NaiveDate. */
  issue_date: string;
  lines: CreateCreditNoteLineInput[];
}

export function listCreditNotes(): Promise<CreditNote[]> {
  return request("GET", "/finance/invoicing/credit-notes", {
    query: currentOrganizationScope(),
  });
}

export function createCreditNote(input: CreateCreditNoteInput): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  invoice_id: string;
  status: string;
}> {
  return request("POST", "/finance/invoicing/credit-notes", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getCreditNote(
  credit_note_id: string,
): Promise<CreditNoteDetail> {
  return request("GET", `/finance/invoicing/credit-notes/${credit_note_id}`, {
    query: currentOrganizationScope(),
  });
}

/** Move a note along draft → sent|void. The sent → applied step is
 * `applyCreditNote` instead (it posts journals). */
export function transitionCreditNote(
  credit_note_id: string,
  status: "sent" | "void",
): Promise<{ id: string; status: string }> {
  return request(
    "POST",
    `/finance/invoicing/credit-notes/${credit_note_id}/transition`,
    { body: { ...currentOrganizationScope(), status } },
  );
}

/** Apply a sent note against its source invoice. Posts the AR adjustment
 * and flips the note to Applied; rejects when the note isn't sent, carries
 * no source invoice, or exceeds the invoice's open balance. */
export function applyCreditNote(
  credit_note_id: string,
): Promise<{ id: string; status: string; journal_entry_id: string }> {
  return request(
    "POST",
    `/finance/invoicing/credit-notes/${credit_note_id}/apply`,
    { body: currentOrganizationScope() },
  );
}

// --- Recurring invoices (Phase 3 Money-in) --------------------------------
// Schedules with template lines; the generate run drafts + issues one
// standard invoice per due occurrence and posts the standard invoice
// journals (source_type='invoice'). List/get shapes match
// `recurring_invoice_schedule_response` in crate_surfaces.rs. Note the list
// is scoped by organization only (no legal_entity_id).

export interface RecurringInvoiceLine {
  id: string;
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  tax_cents: number;
  sort_order: number;
}

export interface RecurringInvoiceSchedule {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  customer_name: string;
  crm_contact_id?: string;
  frequency: string;
  start_date: string;
  end_date?: string;
  next_run_date?: string;
  last_run_date?: string;
  occurrences_generated: number;
  max_occurrences?: number;
  status: string;
  due_in_days: number;
  currency_code: string;
  lines: RecurringInvoiceLine[];
}

export interface CreateRecurringLineInput {
  account_id: string;
  description: string;
  amount_minor: number;
}

interface CreateRecurringScheduleInput {
  customer_name: string;
  crm_contact_id?: string;
  frequency: string;
  /** Calendar dates (YYYY-MM-DD). */
  start_date: string;
  end_date?: string;
  max_occurrences?: number;
  due_in_days: number;
  currency_code?: string;
  lines: CreateRecurringLineInput[];
}

export const RECURRING_FREQUENCIES = [
  "weekly",
  "fortnightly",
  "monthly",
  "quarterly",
  "semi_annual",
  "annual",
] as const;

export function listRecurringSchedules(): Promise<RecurringInvoiceSchedule[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/recurring-invoices", {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

export function createRecurringSchedule(
  input: CreateRecurringScheduleInput,
): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  status: string;
}> {
  return request("POST", "/finance/invoicing/recurring-invoices", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getRecurringSchedule(
  schedule_id: string,
): Promise<RecurringInvoiceSchedule> {
  return request(
    "GET",
    `/finance/invoicing/recurring-invoices/${schedule_id}`,
    { query: currentOrganizationScope() },
  );
}

/** Toggle a plan between active and paused. A completed plan is rejected.
 * A paused plan is left out of the generate run. */
export function pauseRecurringSchedule(
  schedule_id: string,
): Promise<{ id: string; status: string }> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/recurring-invoices/${schedule_id}/pause`,
    {
      body: {
        organization_id: scope.organization_id,
      },
    },
  );
}

export interface RecurringGenerateResult {
  generated_count: number;
  generated: Array<{
    schedule_id: string;
    invoice_id: string;
    journal_entry_id?: string;
  }>;
}

/** Operator-triggered run: generates due invoices (bounded by
 * max_occurrences/end_date with catch-up). A locked period skips posting
 * but still advances the schedule. */
export function generateRecurringInvoices(
  input: { as_of?: string } = {},
): Promise<RecurringGenerateResult> {
  return request("POST", "/finance/invoicing/recurring-invoices/generate", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

// --- Recurring plan occurrence generate + status ---------------------------
// Per-plan generation for one frozen due occurrence: exactly one draft for
// the selected plan, advancing the plan atomically and never posting,
// sending, or paying. The read-only sibling answers whether that exact
// occurrence already has a document. Both send the caller-captured Console
// (organization) scope with the plan's own stored books legal
// entity, and both are validated against the exact plan id, occurrence date,
// and document kind the caller froze — anything else is an unknown outcome,
// not a success.

type RecurringPlanKind = "invoice" | "bill";

interface RecurringOccurrenceResult {
  schedule_id: string;
  occurrence_date: string;
  created: boolean;
  document_id: string;
  document_status: string;
  journal_entry_id: string | null;
  schedule_status: string;
  next_run_date: string;
  occurrences_generated: number;
}

interface RecurringOccurrenceStatus {
  schedule_id: string;
  occurrence_date: string;
  exists: boolean;
  document_id: string | null;
  document_status: string | null;
  journal_entry_id: string | null;
  schedule_status: string;
  next_run_date: string;
  occurrences_generated: number;
}

/** The organization captured when the review opened; never re-read
 * from the live organization for a request that belongs to an older scope. */
interface RecurringOccurrenceRequestScope {
  organization_id: string;
}

interface RecurringOccurrenceExpectation {
  schedule_id: string;
  occurrence_date: string;
  kind: RecurringPlanKind;
}

const RECURRING_SCHEDULE_STATUSES: readonly string[] = [
  "active",
  "paused",
  "completed",
];

const RECURRING_DOCUMENT_STATUSES: Record<
  RecurringPlanKind,
  readonly string[]
> = {
  invoice: ["draft", "sent", "paid", "overdue"],
  bill: ["draft", "received", "paid", "overdue", "void"],
};

/** Exact calendar day (`YYYY-MM-DD`), reusing the conversion-day parser so
 * `2026-10-01T00:00:00Z` and impossible dates are not prefix-accepted. */
function isCalendarDay(value: unknown): value is string {
  return typeof value === "string" && nextIsoDay(value) !== null;
}

function occurrenceRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function occurrenceCommon(
  input: Record<string, unknown>,
  expected: RecurringOccurrenceExpectation,
): boolean {
  return (
    input.schedule_id === expected.schedule_id &&
    input.occurrence_date === expected.occurrence_date &&
    isCalendarDay(input.occurrence_date) &&
    typeof input.schedule_status === "string" &&
    RECURRING_SCHEDULE_STATUSES.includes(input.schedule_status) &&
    typeof input.next_run_date === "string" &&
    input.next_run_date.length > 0 &&
    Number.isSafeInteger(input.occurrences_generated) &&
    (input.occurrences_generated as number) >= 0
  );
}

function knownDocumentStatus(
  kind: RecurringPlanKind,
  value: unknown,
): value is string {
  return (
    typeof value === "string" && RECURRING_DOCUMENT_STATUSES[kind].includes(value)
  );
}

/** Narrows a generate response to this exact plan occurrence, or null when
 * the response is malformed or belongs to a different schedule/date/kind.
 * A `created` result must be the new draft the contract promises: draft
 * status and no journal. A replay may be any known document status. */
export function validateRecurringOccurrenceResult(
  value: unknown,
  expected: RecurringOccurrenceExpectation,
): RecurringOccurrenceResult | null {
  const input = occurrenceRecord(value);
  if (!input || !occurrenceCommon(input, expected)) return null;
  if (typeof input.created !== "boolean") return null;
  if (typeof input.document_id !== "string" || !input.document_id) return null;
  if (!knownDocumentStatus(expected.kind, input.document_status)) return null;
  if (
    input.journal_entry_id !== null &&
    typeof input.journal_entry_id !== "string"
  ) {
    return null;
  }
  if (
    input.created &&
    (input.document_status !== "draft" || input.journal_entry_id !== null)
  ) {
    return null;
  }
  return {
    schedule_id: expected.schedule_id,
    occurrence_date: expected.occurrence_date,
    created: input.created,
    document_id: input.document_id,
    document_status: input.document_status,
    journal_entry_id: input.journal_entry_id,
    schedule_status: input.schedule_status as string,
    next_run_date: input.next_run_date as string,
    occurrences_generated: input.occurrences_generated as number,
  };
}

/** Narrows a read-only occurrence status response to this exact plan
 * occurrence, or null when the response is malformed or belongs to a
 * different schedule/date/kind. An absent occurrence carries no document at
 * all — id, status, and journal must all be null. */
export function validateRecurringOccurrenceStatus(
  value: unknown,
  expected: RecurringOccurrenceExpectation,
): RecurringOccurrenceStatus | null {
  const input = occurrenceRecord(value);
  if (!input || !occurrenceCommon(input, expected)) return null;
  if (typeof input.exists !== "boolean") return null;
  if (input.exists) {
    if (typeof input.document_id !== "string" || !input.document_id) return null;
    if (!knownDocumentStatus(expected.kind, input.document_status)) return null;
  } else if (
    input.document_id !== null ||
    input.document_status !== null ||
    input.journal_entry_id !== null
  ) {
    return null;
  }
  if (
    input.journal_entry_id !== null &&
    typeof input.journal_entry_id !== "string"
  ) {
    return null;
  }
  return {
    schedule_id: expected.schedule_id,
    occurrence_date: expected.occurrence_date,
    exists: input.exists,
    document_id: input.document_id as string | null,
    document_status: input.document_status as string | null,
    journal_entry_id: input.journal_entry_id,
    schedule_status: input.schedule_status as string,
    next_run_date: input.next_run_date as string,
    occurrences_generated: input.occurrences_generated as number,
  };
}

function occurrenceRequestScope(
  requestScope: RecurringOccurrenceRequestScope | undefined,
): RecurringOccurrenceRequestScope {
  if (requestScope) return requestScope;
  const scope = currentOrganizationScope();
  return {
    organization_id: scope.organization_id,
  };
}

export function generateRecurringInvoiceOccurrence(
  schedule_id: string,
  legal_entity_id: string,
  occurrence_date: string,
  requestScope?: RecurringOccurrenceRequestScope,
): Promise<RecurringOccurrenceResult> {
  const scope = occurrenceRequestScope(requestScope);
  return request(
    "POST",
    `/finance/invoicing/recurring-invoices/${schedule_id}/generate`,
    {
      body: {
        organization_id: scope.organization_id,
        legal_entity_id,
        occurrence_date,
      },
    },
  );
}

export function getRecurringInvoiceOccurrence(
  schedule_id: string,
  legal_entity_id: string,
  occurrence_date: string,
  requestScope?: RecurringOccurrenceRequestScope,
): Promise<RecurringOccurrenceStatus> {
  const scope = occurrenceRequestScope(requestScope);
  return request(
    "GET",
    `/finance/invoicing/recurring-invoices/${schedule_id}/occurrences/${occurrence_date}`,
    {
      query: {
        organization_id: scope.organization_id,
        legal_entity_id,
      },
    },
  );
}

// --- Customer prepayments (Phase 3 Money-in) ------------------------------
// Cash before invoice: receipt posts DR bank `1100` / CR deferred revenue
// `2300` (liability, never revenue); application posts DR deferred `2300` /
// CR AR `1200` with no Payment row (no cash moves). Shapes match
// `prepayment_response` / `prepayment_application_response` in
// crate_surfaces.rs.

export interface Prepayment {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  customer_name: string;
  crm_contact_id?: string;
  amount_minor: number;
  applied_amount_minor: number;
  unapplied_minor: number;
  currency_code: string;
  method: string;
  received_at: string;
  status: string;
}

export interface PrepaymentApplication {
  id: string;
  organization_id: string;
  prepayment_id: string;
  invoice_id: string;
  amount_minor: number;
  applied_at: string;
}

export interface PrepaymentDetail extends Prepayment {
  applications: PrepaymentApplication[];
}

interface RecordPrepaymentInput {
  customer_name: string;
  crm_contact_id?: string;
  amount_minor: number;
  method: string;
  received_at: string;
}

export function listPrepayments(): Promise<Prepayment[]> {
  return request("GET", "/finance/invoicing/prepayments", {
    query: currentOrganizationScope(),
  });
}

export function recordPrepayment(input: RecordPrepaymentInput): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  amount_minor: number;
  status: string;
  journal_entry_id?: string;
}> {
  return request("POST", "/finance/invoicing/prepayments", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getPrepayment(
  prepayment_id: string,
): Promise<PrepaymentDetail> {
  return request("GET", `/finance/invoicing/prepayments/${prepayment_id}`, {
    query: currentOrganizationScope(),
  });
}

/** Apply part (or all) of a prepayment against a later sent/overdue
 * invoice. Must fit both the unapplied remainder and the invoice's open
 * balance; both sides must share the legal entity. */
export function applyPrepayment(
  prepayment_id: string,
  input: { invoice_id: string; amount_minor: number; applied_at?: string },
): Promise<{
  id: string;
  prepayment_id: string;
  invoice_id: string;
  amount_minor: number;
  journal_entry_id?: string;
}> {
  return request(
    "POST",
    `/finance/invoicing/prepayments/${prepayment_id}/apply`,
    { body: { ...currentOrganizationScope(), ...input } },
  );
}

// --- Revenue schedules / spread-out income (Phase 3 Money-in) -------------
// Deferred-revenue recognition timetables over an invoice. Recognizing the
// next period posts DR deferred `2300` / CR the resolved revenue account
// (source_type='revenue_recognition') and rolls the schedule forward,
// completing it at the last period. Shapes match
// `revenue_schedule_response` / `revenue_entry_response` in
// crate_surfaces.rs. The list is scoped by organization with an
// optional per-invoice filter (no legal_entity_id).

export interface RevenueScheduleEntry {
  id: string;
  period_date: string;
  amount_minor: number;
  journal_entry_id?: string;
  recognized: boolean;
  sort_order: number;
}

export interface RevenueSchedule {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  invoice_id: string;
  invoice_line_id?: string;
  total_minor: number;
  recognized_minor: number;
  start_date: string;
  end_date: string;
  method: string;
  status: string;
  entries: RevenueScheduleEntry[];
}

interface CreateRevenueScheduleInput {
  invoice_id: string;
  invoice_line_id?: string;
  total_minor: number;
  /** Calendar dates (YYYY-MM-DD); the period count derives from the range. */
  start_date: string;
  end_date: string;
  method?: string;
}

export const REVENUE_METHODS = [
  "straight_line",
  "milestone",
  "on_completion",
] as const;

export function listRevenueSchedules(
  params: { invoice_id?: string } = {},
): Promise<RevenueSchedule[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/revenue-schedules", {
    query: {
      organization_id: scope.organization_id,
      ...(params.invoice_id ? { invoice_id: params.invoice_id } : {}),
    },
  });
}

export function createRevenueSchedule(
  input: CreateRevenueScheduleInput,
): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  invoice_id: string;
  status: string;
}> {
  return request("POST", "/finance/invoicing/revenue-schedules", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getRevenueSchedule(
  schedule_id: string,
): Promise<RevenueSchedule> {
  return request("GET", `/finance/invoicing/revenue-schedules/${schedule_id}`, {
    query: currentOrganizationScope(),
  });
}

/** Post the next unrecognized period. A legal entity with no
 * revenue/deferred-revenue account configured yields a 409, not a
 * silent no-op. */
export function recognizeRevenueEntry(
  schedule_id: string,
): Promise<{ schedule_id: string; journal_entry_id: string }> {
  return request(
    "POST",
    `/finance/invoicing/revenue-schedules/${schedule_id}/recognize`,
    { body: currentOrganizationScope() },
  );
}

// --- Supplier credits / debit notes (Phase 4 Money-out) ------------------
// Draft a note against one source bill, mark it sent (or void), then apply
// it — the apply step posts the AP adjustment journals (DR AP `2100` / CR
// each line's expense account / CR input VAT `1500` for the tax legs,
// source_type='debit_note', the exact reversal of `post_bill_to_ledger`)
// and flips the note to Applied (the idempotency guard: a second apply is
// rejected). List/get shapes match `supplier_credit_response` + the line
// projection in `get_supplier_credit` in crate_surfaces.rs. The list is
// scoped by organization + legal entity (CreditNotesQuery).

export interface SupplierCredit {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  supplier_credit_number: string;
  supplier_name: string;
  status: string;
  total_minor: number;
  issue_date: string;
  related_bill_id?: string;
  crm_contact_id?: string;
}

export interface SupplierCreditLine {
  id: string;
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  tax_cents: number;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
}

export interface SupplierCreditDetail extends SupplierCredit {
  lines: SupplierCreditLine[];
}

export interface CreateSupplierCreditLineInput {
  account_id: string;
  description: string;
  amount_minor: number;
  sku_id?: string;
  quantity_amount?: number;
  quantity_scale?: number;
  unit?: InvoiceLineUnit;
  unit_price_cents?: number;
}

interface CreateSupplierCreditInput {
  /** The source bill is mandatory: it must exist and belong to the same
   * legal entity (anything else is a 404). */
  bill_id: string;
  supplier_name: string;
  /** Calendar date (YYYY-MM-DD); the backend parses a NaiveDate. */
  issue_date: string;
  lines: CreateSupplierCreditLineInput[];
}

export function listSupplierCredits(): Promise<SupplierCredit[]> {
  return request("GET", "/finance/invoicing/supplier-credits", {
    query: currentOrganizationScope(),
  });
}

export function createSupplierCredit(
  input: CreateSupplierCreditInput,
): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  bill_id: string;
  status: string;
}> {
  return request("POST", "/finance/invoicing/supplier-credits", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getSupplierCredit(
  supplier_credit_id: string,
): Promise<SupplierCreditDetail> {
  return request(
    "GET",
    `/finance/invoicing/supplier-credits/${supplier_credit_id}`,
    { query: currentOrganizationScope() },
  );
}

/** Move a note along draft → sent|void. The sent → applied step is
 * `applySupplierCredit` instead (it posts journals). */
export function transitionSupplierCredit(
  supplier_credit_id: string,
  status: "sent" | "void",
): Promise<{ id: string; status: string }> {
  return request(
    "POST",
    `/finance/invoicing/supplier-credits/${supplier_credit_id}/transition`,
    { body: { ...currentOrganizationScope(), status } },
  );
}

/** Apply a sent note against its source bill. Posts the AP adjustment and
 * flips the note to Applied; rejects when the note isn't sent, the bill
 * can't receive relief, or the total exceeds the bill's open balance. A
 * legal entity with no AP account yields a 409 (`no_ledger_account`). */
export function applySupplierCredit(
  supplier_credit_id: string,
): Promise<{ id: string; status: string; journal_entry_id: string }> {
  return request(
    "POST",
    `/finance/invoicing/supplier-credits/${supplier_credit_id}/apply`,
    { body: currentOrganizationScope() },
  );
}

// --- Recurring bills (Phase 4 Money-out) ---------------------------------
// Schedules with template lines; the generate run drafts one standard bill
// per due occurrence, receives it, and posts the standard bill journals
// (source_type='invoice' path via `post_bill_to_ledger`). List/get shapes
// match `recurring_bill_schedule_response` in crate_surfaces.rs. Note the
// list is scoped by organization only (no legal_entity_id).

export interface RecurringBillLine {
  id: string;
  account_id: string;
  description: string;
  amount_minor: number;
  tax_rate_id?: string;
  tax_cents: number;
  sort_order: number;
}

export interface RecurringBillSchedule {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  supplier_name: string;
  crm_contact_id?: string;
  frequency: string;
  start_date: string;
  end_date?: string;
  next_run_date?: string;
  last_run_date?: string;
  occurrences_generated: number;
  max_occurrences?: number;
  status: string;
  due_in_days: number;
  currency_code: string;
  lines: RecurringBillLine[];
}

export interface CreateRecurringBillLineInput {
  account_id: string;
  description: string;
  amount_minor: number;
}

interface CreateRecurringBillScheduleInput {
  supplier_name: string;
  crm_contact_id?: string;
  frequency: string;
  /** Calendar dates (YYYY-MM-DD). */
  start_date: string;
  end_date?: string;
  max_occurrences?: number;
  due_in_days: number;
  currency_code?: string;
  lines: CreateRecurringBillLineInput[];
}

export function listRecurringBillSchedules(): Promise<RecurringBillSchedule[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/invoicing/recurring-bills", {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

export function createRecurringBillSchedule(
  input: CreateRecurringBillScheduleInput,
): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  status: string;
}> {
  return request("POST", "/finance/invoicing/recurring-bills", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getRecurringBillSchedule(
  schedule_id: string,
): Promise<RecurringBillSchedule> {
  return request("GET", `/finance/invoicing/recurring-bills/${schedule_id}`, {
    query: currentOrganizationScope(),
  });
}

/** Toggle a plan between active and paused. A completed plan is rejected.
 * A paused plan is left out of the generate run. */
export function pauseRecurringBillSchedule(
  schedule_id: string,
): Promise<{ id: string; status: string }> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/recurring-bills/${schedule_id}/pause`,
    {
      body: {
        organization_id: scope.organization_id,
      },
    },
  );
}

export interface RecurringBillGenerateResult {
  generated_count: number;
  generated: Array<{
    schedule_id: string;
    bill_id: string;
    journal_entry_id?: string;
  }>;
}

/** Operator-triggered run: generates due bills (bounded by
 * max_occurrences/end_date with catch-up). A locked period skips posting
 * but still advances the schedule; any other error aborts the run. */
export function generateRecurringBills(
  input: { as_of?: string } = {},
): Promise<RecurringBillGenerateResult> {
  return request("POST", "/finance/invoicing/recurring-bills/generate", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

/** Per-plan bill generation for the frozen occurrence. See the recurring
 * invoice occurrence helpers for the identity/shape contract. */
export function generateRecurringBillOccurrence(
  schedule_id: string,
  legal_entity_id: string,
  occurrence_date: string,
  requestScope?: RecurringOccurrenceRequestScope,
): Promise<RecurringOccurrenceResult> {
  const scope = occurrenceRequestScope(requestScope);
  return request(
    "POST",
    `/finance/invoicing/recurring-bills/${schedule_id}/generate`,
    {
      body: {
        organization_id: scope.organization_id,
        legal_entity_id,
        occurrence_date,
      },
    },
  );
}

export function getRecurringBillOccurrence(
  schedule_id: string,
  legal_entity_id: string,
  occurrence_date: string,
  requestScope?: RecurringOccurrenceRequestScope,
): Promise<RecurringOccurrenceStatus> {
  const scope = occurrenceRequestScope(requestScope);
  return request(
    "GET",
    `/finance/invoicing/recurring-bills/${schedule_id}/occurrences/${occurrence_date}`,
    {
      query: {
        organization_id: scope.organization_id,
        legal_entity_id,
      },
    },
  );
}

// --- Expense reports / claims approval (Phase 4 Money-out) ---------------
// The finance approval queue for employee expense claims. Expense capture
// lives in HR — this app never creates a report, it only moves submitted
// reports along submit|approve|reject and posts approved reimbursements to
// the ledger. List shape matches `expense_report_response` in
// crate_surfaces.rs (scoped by organization); the approve/reject bodies are
// `{organization_id, note?}`.

export interface ExpenseReportItem {
  id: string;
  category?: string;
  amount_minor: number;
  occurred_at: string;
  receipt_object_id?: string;
  expense_account_id?: string;
}

export interface ExpenseReport {
  id: string;
  organization_id: string;
  legal_entity_id?: string;
  title: string;
  description?: string;
  submitted_by: string;
  status: string;
  total_minor: number;
  items: ExpenseReportItem[];
}

export function listExpenseReports(): Promise<ExpenseReport[]> {
  const scope = currentOrganizationScope();
  return request("GET", "/finance/expenses/reports", {
    query: {
      organization_id: scope.organization_id,
    },
  });
}

function expenseReportAction(
  report_id: string,
  action: "approve" | "reject",
  input: { note?: string } = {},
): Promise<{ ok: boolean }> {
  const scope = currentOrganizationScope();
  return request("POST", `/finance/expenses/reports/${report_id}/${action}`, {
    body: {
      organization_id: scope.organization_id,
      ...(input.note ? { note: input.note } : {}),
    },
  });
}

export function approveExpenseReport(
  report_id: string,
  input: { note?: string } = {},
): Promise<{ ok: boolean }> {
  return expenseReportAction(report_id, "approve", input);
}

export function rejectExpenseReport(
  report_id: string,
  input: { note?: string } = {},
): Promise<{ ok: boolean }> {
  return expenseReportAction(report_id, "reject", input);
}

/** Settle an approved report by posting the employee-payable-to-bank journal. */
export function reimburseExpenseReport(
  report_id: string,
  input: {
    legal_entity_id: string;
    paid_at: string;
    bank_account_id?: string;
  },
): Promise<{ id: string; status: string; journal_entry_id: string }> {
  const scope = currentOrganizationScope();
  return request("POST", `/finance/expenses/reports/${report_id}/reimburse`, {
    body: { ...scope, ...input },
  });
}

// --- Expense reimbursement postings (Phase 4 Money-out) ------------------
// Approved-line facts (read) plus the invoicing-owned journal mutation that
// consumes them (write). The read lists every approved line awaiting
// posting; the post writes one balanced entry per line and reports how
// many posted. Scoped by the organization scope (organization +
// legal entity).

export interface ReimbursementPosting {
  organization_id: string;
  legal_entity_id: string;
  report_id: string;
  line_id: string;
  title: string;
  expense_account_id?: string;
  expense_account_code?: string;
  amount_cents: number;
  payable_account_code: string;
  occurred_at: string;
  approved_at: string;
}

export function listReimbursementPostings(): Promise<ReimbursementPosting[]> {
  return request("GET", "/finance/expenses/reimbursement-postings", {
    query: currentOrganizationScope(),
  });
}

export function postExpenseReimbursements(): Promise<{
  posted_count: number;
  journal_entry_ids: string[];
}> {
  return request("POST", "/finance/invoicing/expense-reimbursements/post", {
    body: currentOrganizationScope(),
  });
}

// --- Stock costs / landed costs (Phase 4 Money-out) ----------------------
// Freight, duty, insurance and other shipping costs capitalized into stock
// value. Draft a cost with apportioned components, then post it — posting
// writes one balanced entry (DR the resolved inventory account / CR AP
// `2100` when unpaid or CR the caller's cash account when paid,
// source_type='stock_cost') and flips the cost to Posted (the idempotency
// guard: re-posting is rejected). The inventory target must resolve to an
// asset-type chart account — never COGS/expense. List/get shapes match
// `stock_cost_response` in crate_surfaces.rs; the number is server-assigned
// (`LC-…`). `receipt_reference` links synced receipt lines opaquely (the
// inventory movements live under the organization tenancy root while the AP
// books live under organization + legal entity — the caller apportions, the
// reference preserves the audit trail).

export const STOCK_COST_COMPONENT_KINDS = [
  "freight",
  "duty",
  "insurance",
  "other",
] as const;

export interface StockCostComponent {
  id: string;
  kind: string;
  description: string;
  amount_minor: number;
  sort_order: number;
}

export interface StockCost {
  id: string;
  organization_id: string;
  legal_entity_id: string;
  stock_cost_number: string;
  description: string;
  receipt_reference?: string;
  receipt_number?: string;
  inventory_account_id: string;
  cash_account_id?: string;
  status: string;
  total_minor: number;
  cost_date: string;
  currency_code: string;
  posted_journal_entry_id?: string;
  components: StockCostComponent[];
}

export interface CreateStockCostComponentInput {
  kind: string;
  description: string;
  amount_minor: number;
}

interface CreateStockCostInput {
  description: string;
  receipt_reference?: string;
  receipt_number?: string;
  inventory_account_id: string;
  /** Null/omitted = unpaid (posts to what you owe); set = paid from this
   * cash account. */
  cash_account_id?: string;
  /** Calendar date (YYYY-MM-DD). */
  cost_date: string;
  currency_code?: string;
  components: CreateStockCostComponentInput[];
}

export function listStockCosts(): Promise<StockCost[]> {
  return request("GET", "/finance/invoicing/stock-costs", {
    query: currentOrganizationScope(),
  });
}

export function createStockCost(input: CreateStockCostInput): Promise<{
  id: string;
  organization_id: string;
  legal_entity_id: string;
  status: string;
}> {
  return request("POST", "/finance/invoicing/stock-costs", {
    body: { ...currentOrganizationScope(), ...input },
  });
}

export function getStockCost(stock_cost_id: string): Promise<StockCost> {
  return request("GET", `/finance/invoicing/stock-costs/${stock_cost_id}`, {
    query: currentOrganizationScope(),
  });
}

/** Post a drafted cost to the ledger. Flips to Posted; a legal entity with
 * no usable credit account yields a 409 (`no_ledger_account`). */
export function postStockCost(stock_cost_id: string): Promise<{
  id: string;
  status: string;
  journal_entry_id: string;
}> {
  const scope = currentOrganizationScope();
  return request(
    "POST",
    `/finance/invoicing/stock-costs/${stock_cost_id}/post`,
    {
      body: {
        organization_id: scope.organization_id,
      },
    },
  );
}

interface DashboardLayout {
  state: unknown;
  version: number;
  updated_at: string;
}

interface DashboardLayouts {
  personal?: DashboardLayout;
  workspace_default?: DashboardLayout;
  can_write_workspace_default: boolean;
}

export function getDashboardLayouts(): Promise<DashboardLayouts> {
  return request("GET", "/finance/accounting/dashboard-layouts", {
    query: currentOrganizationScope(),
  });
}

export function saveDashboardLayout(input: {
  target: "personal" | "workspace_default";
  state: unknown;
  version?: number;
}): Promise<DashboardLayout> {
  return request("PUT", "/finance/accounting/dashboard-layouts", {
    body: { ...currentOrganizationScope(), ...input },
  });
}
